import React from "react";
import {
    Box,
    Button,
    Checkbox,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    InputAdornment,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {keyframes} from "@emotion/react";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AddIcon from "@mui/icons-material/Add";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import EditNoteIcon from "@mui/icons-material/EditNote";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GavelIcon from "@mui/icons-material/Gavel";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SearchIcon from "@mui/icons-material/Search";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import get from "lodash.get";
import {isNotInsurerReadOnlyUser} from "../../constants/UserAuth";
import PcNonPayablesWorkspace from "./PcNonPayablesWorkspace";
import DynamicConstruct from "./DynamicConstruct";
import {
    MONEY_EPSILON,
    addedSavingIfMarked,
    applyPcNonPayable,
    arrivedNonPayable,
    arrivedPayable,
    baselineOf,
    billAmountOf,
    chargedQuantity,
    clampQuantity,
    committedNonPayableQuantity,
    constructNonPayableQuantity,
    constructObjectNonPayableQuantity,
    constructRemarksOf,
    formatPercent,
    formatRupees,
    isIrdaiListItem,
    isPcBillLineItem,
    isPcNonPayable,
    isPcDisallowed,
    pcNonPayableAmounts,
    pcVerdictTextOf,
    remarksOf,
    restoredPayableQuantity,
    revertPcNonPayable,
    roundMoney,
    rowKey,
    savingOf,
    stagePayableQuantity,
    stageRemarks,
} from "../../utils/pcNonPayable";

/**
 * Procedure Construct Non-Payables - the editor that turns the Procedure Construct's
 * findings into money on the claim.
 *
 * Three fields sort a row between the panel's three lists, asked strictly in this order. The
 * first one to answer decides where the row goes, and the ones below it are not consulted:
 *
 *   1. irdai_payable      false -> IRDAI. Not payable under IRDAI, so it is off the claim before
 *                         the Procedure Construct is consulted, and nothing below can put it
 *                         back. Read only, and never sent.
 *   2. construct_object   a ruling against the line item -> Bill Line Item. `allowed: false`,
 *                         a `status` of DISALLOWED, or a CAPPED one whose `allowed_quantity`
 *                         bites - any one of them is enough, and it stands whatever text the row
 *                         carries beside it. A CAPPED row arrives reading `allowed: true` with
 *                         its verdict still "Payable by Procedure Construct", and belongs here.
 *   3. procedure_construct_payable_text
 *                         "Non-Payable" or "Partially Payable" -> Bill Line Item: what the claim
 *                         is not paying in full, and where a quantity or a remark is adjusted.
 *                         Anything else -> Disallowed, the pool a line item is disallowed *from*.
 *                         The text and not the boolean, because it is the one that can tell a
 *                         partial from a whole.
 *
 * The ladder is `pcRouteOf` - one function with three answers, so a row lands in exactly one
 * list and cannot be shown twice, counted twice, or lost between them. See pcNonPayable.js for
 * how `allowed` and `status` are put together, and for why step 2 asks the ruling rather than the
 * cap - it is what lets a pull-back stick.
 *
 * The first two tables are two ends of one move, not two lists: Add on a Disallowed row flips
 * the flag to false and the row appears above; the x on a Bill Line Item row flips it back to
 * true and the row drops below. The IRDAI list is no end of any move - see below.
 *
 * Everything this panel writes goes onto the row itself, never beside it. The Payable Qty
 * column writes `allowed_units_by_proc_construct` and the verdict that goes with it, the
 * Remarks column writes `remarks` through the same dialog every other change goes through, the
 * two buttons write `procedure_construct_payable`, and the tables read it all back - so the
 * screen is only ever a view of the rows, and nothing can drift out of step with them. Writes are staged in the
 * tariff list until the sheet is saved, or until the panel adjudicates. A row disallowed here
 * and then added back lands on exactly the figures it arrived with; its verdict text is the
 * one thing rewritten rather than restored, because it has to agree with the flag.
 *
 * Only the Bill Line Item table is saved. A line item sitting in the Disallowed table is still
 * payable and nothing here writes to it - it reaches the save payload only once Add has moved
 * it up, so no line item is ever disallowed on the claim without someone choosing it.
 *
 * The third list sits under those two, and it is not part of the move at all: the line items
 * IRDAI itself does not pay - step 1 of the ladder, the rows the tariff grid tints amber. They
 * are here to be read, because a claim's non-payables cannot be reviewed with part of them off
 * screen, and for nothing else:
 *
 *   folded away  closed on arrival, opened by the strip - which carries the figure and the
 *                count either way, so what the fold hides is which line items they are and
 *                never how much. The two lists that carry the decision keep the height until
 *                somebody asks for this one. A search that hits it opens it.
 *   read only    no quantity box, no Add, no x, no remark to edit. There is no decision left to
 *                take - IRDAI has taken it, and the Procedure Construct has no say over it.
 *   never sent   the commit maps over the Bill Line Item table alone, so no IRDAI row can leave
 *                here stamped with a Procedure Construct verdict it never earned.
 *   its own money, kept its own. The Saving card and the save button still total the Bill Line
 *                Item table and nothing else, so what the button promises stays exactly what
 *                pressing it writes. What IRDAI is already not paying is a second figure beside
 *                it, and the two are added up in words in its tooltip rather than silently in
 *                one amount - they are different money, saved by different rules, and one
 *                figure carrying both would be claimed by this panel as its own.
 *
 * Because `irdai_payable` is asked first, none of that is a filter applied afterwards: an IRDAI
 * row cannot reach the Bill Line Item table, so it cannot reach the commit, and its money cannot
 * reach the Saving the save button promises. Every line item is in exactly one list, and no
 * amount is counted twice.
 */

const SORTABLE_COLUMNS = [
    {key: 'bill_line_item', label: 'Bill Line Item', align: 'left', width: '17rem'},
    // No width: under a fixed table layout the unsized column takes whatever the others
    // leave, and the remarks are the longest thing on the row by far. Sorting prose puts
    // nothing useful at the top, so this is the one column that does not offer it.
    //
    // Up to two things are stacked in it, both read-only: what the construct said, greyed and
    // unlabelled, above the ABHI Remarks - which is where every change made in this panel
    // writes its reason, and which is edited by clicking the line rather than typed into the
    // row. Either is absent where nothing was written, so a row carrying neither leaves the
    // cell empty; the header is what names them, not every line.
    {key: 'constructRemarks', label: 'Construct Remarks / ABHI Remarks', align: 'left', sortable: false},
    // Payable units, not the cut. The claim is read in the units it pays for, and this is the
    // field `allowed_units_by_proc_construct` already holds - so 16 / 8 is eight units paid and
    // eight not, 16 / 16 is the whole line item paid, and 16 / 0 is none of it. Sorted on the
    // same figure it prints.
    {key: 'payableUnits', label: 'Charged Qty / Payable Qty', align: 'right', width: '9rem'},
    {key: 'saving', label: 'Saving', align: 'right', width: '6.5rem', whiteSpace: 'nowrap'},
];

// The x is an icon and the Add is a pill, so the two action columns are not the same width.
const REMOVE_COLUMN_WIDTH = '2.5rem';
const ADD_COLUMN_WIDTH = '6rem';

// The tick leads both tables, at one width, so the two lists line up down their left edge.
const CHECK_COLUMN_WIDTH = '2.25rem';

// Small, and the panel's own blue. A tick is a thing you aim at repeatedly down a list, so it
// keeps a real hit area while taking little of the row.
const checkboxSx = {
    padding: '2px',
    color: '#b3c6dd',
    '&.Mui-checked': {color: '#1c5fa8'},
    '&.MuiCheckbox-indeterminate': {color: '#1c5fa8'},
    '& .MuiSvgIcon-root': {fontSize: '1rem'},
};

/**
 * Ten line items are on screen at a time in either list and the rest are a scroll away.
 *
 * Both lists stand on rows of a known height, so "ten rows" is a figure and not a guess:
 * the cap is only ever a maxHeight, so a list holding fewer than ten closes up around what
 * it holds instead of sitting in reserved space, and a longer one scrolls.
 */
const VISIBLE_ROWS = 10;
const TABLE_HEADER_HEIGHT = 38;
const TABLE_ROW_HEIGHT = 46;
const DISALLOWED_ROW_HEIGHT = 42;

// The row borders sit outside the row box once the header is sticky, so they are counted in.
const visibleRowsOf = (rowHeight, headerHeight = 0) =>
    headerHeight + VISIBLE_ROWS * (rowHeight + 1);

/**
 * Ten rows is a ceiling, not a height: the panel is as tall as the window and no taller.
 *
 * The two lists are two ends of one move, so they get the same box - one height, used by both,
 * and the shorter list is the one with room to spare rather than the one squeezed. What there
 * is to share is whatever the title bar, the figures band, the two strips and the footer leave
 * behind, split down the middle. On a tall screen the row count binds and both lists stop at
 * ten; on a short one the window binds and both shrink to fit rather than pushing the panel
 * into a scroll of its own. The floor keeps them readable either way.
 */
const PANEL_CHROME_HEIGHT = 244;
const LIST_MIN_HEIGHT = 150;

const listMaxHeight = (chromeHeight) =>
    `min(${visibleRowsOf(TABLE_ROW_HEIGHT, TABLE_HEADER_HEIGHT)}px, `
    + `max(${LIST_MIN_HEIGHT}px, calc((100vh - ${chromeHeight}px) / 2)))`;

const cellStyle = {fontSize: '0.8rem', padding: '4px 10px', borderBottom: '1px solid #edf1f6'};

const headerCellStyle = {
    ...cellStyle,
    fontSize: '0.6rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: '#41597a',
    background: '#e9eff8',
    borderBottom: '1px solid #d5e0ee',
};

// Each list is a card of its own: a rule around it, and a header strip that says what it is.
const cardStyle = {borderRadius: '10px', borderColor: '#dde6f1', overflow: 'hidden'};

const rowSx = {
    minHeight: TABLE_ROW_HEIGHT,
    backgroundColor: '#ffffff',
    '&:hover': {backgroundColor: '#f4f9ff'},
};

/**
 * A row whose saving the claim already holds - it arrived non-payable and still is. It sits in
 * the same table as the work still to be done, so it has to be told apart from it at a glance:
 * a green wash and a green edge down the left, the colour used for money already banked, and a
 * "Saved" tag on the name itself for anyone who does not read the tint.
 *
 * It stays a live row. Nothing about being already saved makes it unreadable or untouchable -
 * the quantity can still be re-cut and the x still makes it payable again, which is the whole
 * reason a decided row is still on screen.
 */
const decidedRowSx = {
    ...rowSx,
    backgroundColor: '#f4faf6',
    boxShadow: 'inset 3px 0 0 0 #9ccdb0',
    '&:hover': {backgroundColor: '#ecf6f0'},
};

// The tag itself: small, quiet, and beside the name rather than in a column of its own - it is
// a property of the row, not another figure to read down.
const savedTagStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3px',
    marginLeft: '6px',
    padding: '0 5px',
    borderRadius: '999px',
    fontSize: '0.58rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: '#1b7f4b',
    background: '#e6f6ec',
    border: '1px solid #c3e7d1',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
};

// The header sort arrow reads as a sort handle whether or not the column is the sorted one.
const sortLabelSx = {
    '& .MuiTableSortLabel-icon': {opacity: 0.5, fontSize: '0.85rem'},
    '&.Mui-active .MuiTableSortLabel-icon': {opacity: 1, color: '#41597a'},
};

// Figures line up down the column when every digit takes the same width.
const moneyStyle = {fontVariantNumeric: 'tabular-nums'};

// One line, ellipsised - a wrapping name would make its row taller than the rest.
const oneLineStyle = {whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'};

// A line item's name is the one thing on the row you cannot guess from the rest of it, so it
// is the one column that wraps instead of ending in an ellipsis - the whole name is on screen,
// and the row grows by a line to hold it.
//
// Which is also why the name carries no tooltip in any of the three lists: nothing is truncated,
// so there is nothing a hover could reveal, and a tooltip on every row of every list is one more
// thing to dismiss on the way to the figures beside it.
const fullNameStyle = {whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word'};

// The two strips carry the two states: what is still payable is blue, what has been
// disallowed is rose. The palette is the fastest read of which half of the move you are on.
const stripStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '3px 10px',
};

const payableStripStyle = {
    ...stripStyle,
    background: '#eff6fe',
    borderBottom: '1px solid #d3e3f6',
};

const disallowedStripStyle = {
    ...stripStyle,
    background: '#fdf1f1',
    borderBottom: '1px solid #f0d2d2',
};

// ...and what IRDAI is not paying is amber - the colour the tariff grid already tints those
// rows, so a line item is the same colour wherever it is read. Neither blue nor rose, because
// it is neither end of the move: nothing on these rows is anybody's to decide here.
const irdaiStripStyle = {
    ...stripStyle,
    background: '#fff8de',
    borderBottom: '1px solid #f0e2b4',
};

const stripTitleStyle = {fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap'};

const countBadgeStyle = {
    fontSize: '0.6rem',
    fontWeight: 700,
    color: '#ffffff',
    borderRadius: '999px',
    padding: '1px 8px',
    whiteSpace: 'nowrap',
};

const hintStyle = {...oneLineStyle, fontSize: '0.66rem'};

// One green pill for both ends of the panel - the button that opens it and the button that
// commits it: what it saves in bold, what it does underneath, and where it goes on the right.
const ctaSx = {
    textTransform: 'none',
    borderRadius: '10px',
    padding: '5px 12px',
    color: '#ffffff',
    background: 'linear-gradient(135deg, #34a85c 0%, #1e7f45 100%)',
    boxShadow: '0 3px 10px rgba(30, 127, 69, 0.30)',
    '&:hover': {
        background: 'linear-gradient(135deg, #2f9a54 0%, #1a713d 100%)',
        boxShadow: '0 4px 12px rgba(30, 127, 69, 0.38)',
    },
    '&.Mui-disabled': {background: '#cfd8dc', color: '#ffffff', boxShadow: 'none'},
};

/**
 * A reviewer sent up here from a shut ABHI Amount box arrives at the top of a page they were
 * halfway down, with nothing to say which of the controls they were sent to. The pulse answers
 * that: a halo in the button's own green, breathing out three times and stopping. It is the
 * button's colour rather than a warning colour on purpose - it points at a control to press,
 * not at a mistake that has been made. The button's resting shadow is carried through every
 * step so the pill itself does not appear to lift and settle underneath it.
 */
const attentionPulse = keyframes`
    0%   {box-shadow: 0 3px 10px rgba(30, 127, 69, 0.30), 0 0 0 0 rgba(52, 168, 92, 0.55);}
    60%  {box-shadow: 0 3px 10px rgba(30, 127, 69, 0.30), 0 0 0 11px rgba(52, 168, 92, 0);}
    100% {box-shadow: 0 3px 10px rgba(30, 127, 69, 0.30), 0 0 0 0 rgba(52, 168, 92, 0);}
`;

const attentionLift = keyframes`
    from {opacity: 0; transform: translateY(-7px);}
    to   {opacity: 1; transform: translateY(0);}
`;

const attentionCtaSx = {
    ...ctaSx,
    animation: `${attentionPulse} 1.5s ease-out 3`,
};

/**
 * The reason they were sent, said where they were sent to. Built to the panel's own card - 10px
 * radius, the same hairline and ink - and hung under the button on a small arrow, so it reads as
 * this control's message rather than a notice that happens to be on the page.
 */
const attentionCalloutSx = {
    position: 'absolute',
    top: 'calc(100% + 11px)',
    right: 0,
    zIndex: 1000,
    display: 'flex',
    alignItems: 'flex-start',
    gap: '9px',
    width: 'max-content',
    maxWidth: '320px',
    padding: '10px 13px',
    borderRadius: '10px',
    textAlign: 'left',
    background: '#ffffff',
    border: '1px solid #e3e9f1',
    boxShadow: '0 12px 30px rgba(15, 23, 42, 0.16)',
    animation: `${attentionLift} 220ms ease-out`,
    // The arrow is the card's own corner turned 45 degrees - two borders showing, the other two
    // tucked behind the card - so it takes the hairline and the fill with it for free.
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '-6px',
        right: '34px',
        width: '11px',
        height: '11px',
        background: '#ffffff',
        borderLeft: '1px solid #e3e9f1',
        borderTop: '1px solid #e3e9f1',
        transform: 'rotate(45deg)',
    },
};

const attentionIconSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    flexShrink: 0,
    marginTop: '1px',
    borderRadius: '7px',
    color: '#9a6700',
    background: '#fff4d6',
};

const attentionTitleStyle = {
    fontSize: '0.78rem',
    fontWeight: 700,
    color: '#2c3e50',
    lineHeight: 1.25,
};

const attentionBodyStyle = {
    fontSize: '0.7rem',
    fontWeight: 400,
    fontStyle: 'normal',
    color: '#6b7d8f',
    lineHeight: 1.45,
};

/**
 * The construct is the reasoning behind the panel, not a decision taken in it, so it does not
 * get the panel's one green CTA. It sits beside it in the brand's own indigo, outlined - plainly
 * a second button, plainly not the one that commits anything.
 */
const constructButtonSx = {
    textTransform: 'none',
    borderRadius: '10px',
    padding: '4px 12px',
    color: '#4a3fd0',
    background: '#ffffff',
    border: '1px solid #cdc8f4',
    boxShadow: 'none',
    '&:hover': {background: '#f4f2ff', border: '1px solid #b3abee'},
    '&.Mui-disabled': {background: '#f7f8fa', color: '#a9b4c2', border: '1px solid #e3e9f1'},
};

const CONSTRUCT_TOOLTIP = 'The reasoning behind these two lists — the drivers read off the'
    + ' discharge summary, what they allow, and every bill line against those allowances';

const constructDialogPaperSx = {
    width: '92vw',
    maxWidth: '1400px',
    height: '92vh',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 24px 60px rgba(18, 49, 91, 0.22)',
};

const constructBadgeStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '34px',
    height: '34px',
    flexShrink: 0,
    borderRadius: '10px',
    color: '#ffffff',
    background: 'linear-gradient(135deg, #6d5ce7 0%, #4a3fd0 100%)',
    boxShadow: '0 2px 6px rgba(74, 63, 208, 0.35)',
};

// Taking a line item out of the payable list is the quiet move, so it is the quiet control:
// an x that only shows its colour on hover.
const removeButtonSx = {
    width: '22px',
    height: '22px',
    borderRadius: '6px',
    color: '#7d8fa5',
    background: '#f2f5f9',
    '&:hover': {background: '#fdeaea', color: '#c62828'},
};

/**
 * The bulk moves. Each is the strip's own button, and each is the colour of the table it takes
 * rows *out of* - rose to make a set payable again, amber to disallow a set - so the button and
 * the strip it sits on are plainly about the same rows.
 *
 * They appear only with something ticked. A permanently-present bulk button reading "0 selected"
 * is a control that spends most of its life explaining that it cannot be used.
 */
const bulkButtonSx = {
    textTransform: 'none',
    borderRadius: '999px',
    flexShrink: 0,
    minWidth: 0,
    padding: '1px 10px',
    fontSize: '0.7rem',
    fontWeight: 700,
    background: '#ffffff',
    '& .MuiButton-startIcon': {marginRight: '4px', marginLeft: 0},
    '& .MuiButton-startIcon > svg': {fontSize: '0.85rem'},
};

const bulkPayableButtonSx = {
    ...bulkButtonSx,
    color: '#a53535',
    borderColor: '#e8b3b3',
    '&:hover': {borderColor: '#c25151', background: '#fdf3f3'},
};

const bulkDisallowButtonSx = {
    ...bulkButtonSx,
    color: '#b26a00',
    borderColor: '#f0cf94',
    '&:hover': {borderColor: '#e08e0b', background: '#fff8ea'},
};

// Pulling one back is the offer, so it is the one that reads as a button.
const addButtonSx = {
    textTransform: 'none',
    borderRadius: '999px',
    minWidth: 0,
    padding: '1px 10px',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#b26a00',
    background: '#ffffff',
    borderColor: '#f0cf94',
    '&:hover': {borderColor: '#e08e0b', background: '#fff8ea'},
    '& .MuiButton-startIcon': {marginRight: '3px', marginLeft: 0},
    '& .MuiButton-startIcon > svg': {fontSize: '0.85rem'},
};

// A remark is read on the row and written in a dialog, so the line only has to look like the
// sentence it is. It lights up on hover where it can be edited, and sits flat where it cannot.
const remarkLineSx = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '5px',
    minWidth: 0,
    borderRadius: '6px',
    padding: '1px 3px',
    margin: '0 -3px',
};

const editableRemarkLineSx = {
    ...remarkLineSx,
    cursor: 'pointer',
    '&:hover': {background: '#f1f6fc'},
};

const searchFieldSx = {
    flexShrink: 0,
    background: '#ffffff',
    borderRadius: '8px',
    '& .MuiOutlinedInput-root': {borderRadius: '8px'},
    '& .MuiOutlinedInput-notchedOutline': {borderColor: '#d3e3f6'},
};

const disallowedRowSx = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    minHeight: `${DISALLOWED_ROW_HEIGHT}px`,
    padding: '6px 10px',
    borderBottom: '1px solid #f2f5f9',
    backgroundColor: '#ffffff',
    '&:hover': {backgroundColor: '#fdf7f7'},
};

/**
 * The strip is the fold's own control, so it is the one strip on the panel that reacts to a
 * pointer - the whole width of it is the hit area, because a chevron alone is a target the
 * reader has to aim at. The rows below it stay inert; only the header does anything.
 *
 * No width is set, and it must not be: this screen does not load MUI's CssBaseline, so nothing
 * has been put on `box-sizing` and a div is still the browser's default content-box. `width:
 * 100%` on a padded strip therefore measures 100% *plus* the padding, and the card it sits in
 * clips its overflow - which took the amount off the right-hand end of the strip and out of
 * sight. The strip is a block element in the card, so it already spans it.
 */
const irdaiHeaderSx = {
    ...irdaiStripStyle,
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {background: '#fdf3d4'},
};

// The same row as the pool above it, in the amber. No hover lift: there is nothing to click.
const irdaiRowSx = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    minHeight: `${DISALLOWED_ROW_HEIGHT}px`,
    padding: '6px 10px',
    borderBottom: '1px solid #f7f2e4',
    backgroundColor: '#ffffff',
};

const emptyNoteStyle = {
    fontSize: '0.75rem',
    color: '#8195ad',
    padding: '14px 12px',
    textAlign: 'center',
};

/**
 * The Add column's figure. A gain is offered with the sign of an offer; anything else - a line
 * item that adds nothing, or one that would take money back off the claim - is stated flat, so
 * a zero or a credit note is never advertised as money to be had.
 */
const gainLabel = (gain) => (gain > MONEY_EPSILON ? `+ ${formatRupees(gain)}` : formatRupees(gain));

/**
 * What disallowing this row would do, spelled out. The figure beside the charged units is what
 * would be left payable, so that is what this leads with - this row is still in the pool, so
 * every word of it is what pressing Add would make true, not what is true now.
 */
const describeSplit = (item) => {
    if (item.payableUnits <= 0) {
        return `Charged ${item.charged} — none payable: ${formatRupees(item.projected.savings)} saved.`;
    }
    const overcharge = roundMoney(item.projected.savings - item.projected.pcSaving);
    return `Charged ${item.charged} — ${item.payableUnits} payable, ${item.nonPayableUnits} not, `
        + `saving ${formatRupees(item.projected.pcSaving)}; the ${item.payableUnits} payable stay at `
        + `tariff for ${formatRupees(overcharge)} overcharged: `
        + `${formatRupees(item.projected.savings)} saved in all.`;
};

/**
 * ...and the same sentence for a row the decision has already landed on, in the past tense.
 *
 * It closes on what the box will accept, because the box is the one thing on the row that can
 * still be changed: settling it at the charged units is not a smaller cut, it is no cut at all,
 * and the row leaves this table for the pool below.
 */
const describeApplied = (item) => {
    const reach = item.payableUnits <= 0
        ? `all ${item.charged} charged`
        : `${item.nonPayableUnits} of ${item.charged} charged`;
    return `${item.verdict || 'Non-Payable by Procedure Construct'} — ${item.payableUnits} payable, `
        + `${reach} disallowed, ${formatRupees(item.saving)} saved on `
        + `${formatRupees(item.billAmount)} billed. Set the payable units to ${item.charged} to make`
        + ` the whole line item payable again and return it to the pool below.`;
};

/**
 * An IRDAI non-payable line item, read off the row exactly as it stands.
 *
 * No baseline is taken and nothing is projected, because nothing here can be decided: the row
 * is described so it can be read, and the shape stops at the four things the list prints. It
 * carries `charged` and `billAmount` so `matchesSearch` and the tooltips - written for the two
 * editable lists - work on it unchanged.
 *
 * `amount` is the bill, not the row's stored `savings`. IRDAI not paying a line item means the
 * whole line is not paid, so the bill is the figure, and it is the one figure that cannot go
 * stale. Where the row's own saving disagrees the tooltip says both rather than picking one
 * quietly - a row carrying a different amount is worth seeing, not worth hiding.
 */
const describeIrdaiItem = (row) => {
    const billAmount = billAmountOf(row);
    return {
        key: rowKey(row),
        row,
        charged: chargedQuantity(row),
        billAmount,
        amount: roundMoney(billAmount),
        storedSaving: roundMoney(savingOf(row)),
        bill_line_item: String(get(row, ['bill_line_item'], '')),
        constructRemarks: constructRemarksOf(row),
        remarks: remarksOf(row),
    };
};

/** Why this row is in the amber list, and what the claim is not paying on it. */
const describeIrdaiRow = (item) => {
    const stated = Math.abs(item.storedSaving - item.amount) > MONEY_EPSILON
        ? ` The row carries ${formatRupees(item.storedSaving)} as its saving.`
        : '';
    return `Not payable under IRDAI — the claim pays nothing on this line, so all`
        + ` ${formatRupees(item.billAmount)} billed across ${item.charged} unit(s) is already`
        + ` off the claim. The Procedure Construct has no say over it and nothing here can be`
        + ` changed or saved.${stated}`;
};

/**
 * The quantity a decision on a row would stand on, read off the row itself: what
 * `allowed_units_by_proc_construct` leaves unallowed, else the whole line item. A figure typed
 * into the Payable column is written to that same field, so the column and the row can never
 * disagree - and nothing about it depends on the panel, so it lives out here.
 */
const effectiveQuantity = (row) => {
    const charged = chargedQuantity(row);
    const byConstruct = constructNonPayableQuantity(row);
    return byConstruct === null ? charged : clampQuantity(byConstruct, charged);
};

/**
 * Is this a partial rather than a whole line item either way? Only then is the box amber: some
 * units payable and some not is the one state worth a second glance, where a line item wholly
 * paid or wholly disallowed is not.
 */
const isPartialCut = (charged, payableUnits) =>
    charged > 0 && payableUnits > 0 && payableUnits < charged;

const matchesSearch = (item, needle) => needle.length === 0 ||
    item.bill_line_item.toLowerCase().includes(needle) ||
    item.constructRemarks.toLowerCase().includes(needle) ||
    String(get(item.row, ['master_category'], '')).toLowerCase().includes(needle);

/**
 * The payable units cell. It keeps what is being typed to itself and settles the figure on blur
 * or Enter, so the editor never repaints the row mid-keystroke. An empty box puts the row back
 * on the construct's figure, or on the whole line item.
 *
 * The figure is what stays payable, so both ends of the range are real answers: nought is the
 * whole line item disallowed, and the charged units are the whole line item payable - which is
 * the one settlement that moves the row out of this table. That is why there is no floor of one
 * here; a non-payable column needed one, because a decision that cuts nothing is not a decision,
 * but a payable column has to be able to say nought is payable.
 */
const PayableQuantityInput = ({charged, quantity, revision, isCut, disabled, onSettle, onClear}) => {
    const [text, setText] = React.useState(String(quantity));
    // What was last written for this row. Leaving the box is not the same as changing it, and
    // the figure it opens on is a real one - for most rows the whole line item - so settling it
    // unchanged would write a decision nobody typed.
    const settledRef = React.useRef(quantity);
    const ceiling = charged > 0 ? Math.floor(charged) : null;

    React.useEffect(() => {
        setText(String(quantity));
        settledRef.current = quantity;
        // `revision` never means anything on its own - the parent bumps it when a change was
        // asked for and then abandoned, and the box has a figure in it that no row carries. It
        // is not enough to wait on `quantity`: a cancelled change leaves the row exactly as it
        // was, so nothing else here would ever fire.
    }, [quantity, revision]);

    /**
     * The column takes whole units only, and a figure past the charged units is not a
     * decision that can be taken - both are refused as they are typed rather than
     * corrected after the fact, so the box never shows a number that cannot stand.
     */
    const accept = (typed) => {
        const digits = String(typed).replace(/[^0-9]/g, '');
        if (digits === '') {
            setText('');
            return;
        }
        const value = Number(digits);
        setText(String(ceiling !== null ? Math.min(value, ceiling) : value));
    };

    const settle = () => {
        if (String(text).trim() === '') {
            onClear();
            return;
        }
        // Nought up to the charged units, both ends included: `clampQuantity` is that range.
        const accepted = clampQuantity(text, charged);
        // Show what was accepted, not what was typed.
        setText(String(accepted));
        if (accepted === settledRef.current) {
            return;
        }
        settledRef.current = accepted;
        onSettle(accepted);
    };

    return (
        <TextField
            type="number"
            size="small"
            value={text}
            disabled={disabled}
            onChange={(e) => accept(e.target.value)}
            onBlur={settle}
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    settle();
                }
            }}
            inputProps={{
                min: 0,
                max: ceiling !== null ? ceiling : undefined,
                step: 1,
                inputMode: 'numeric',
                style: {textAlign: 'right', padding: '5px 8px'},
            }}
            sx={{
                width: '4.5rem',
                background: '#ffffff',
                '& .MuiOutlinedInput-root': {borderRadius: '8px'},
                '& .MuiOutlinedInput-notchedOutline': {borderColor: isCut ? '#f0b978' : '#d5e0ee'},
                '&:hover .MuiOutlinedInput-notchedOutline': {borderColor: '#9fbcdd'},
            }}
            InputProps={{
                style: {
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    color: isCut ? '#ed6c02' : '#12315b',
                    ...moneyStyle,
                },
            }}
        />
    );
};

/**
 * Is there nothing to read on this row? `RemarkLine` prints nothing where nothing was written,
 * so a row carrying neither remark would leave the column blank on its own. Asked here, once,
 * so the dash that stands in for it is decided by the same emptiness the lines are.
 */
const hasNoRemark = (item) => String(item.constructRemarks || '').trim().length === 0
    && String(item.remarks || '').trim().length === 0;

/**
 * One remark on a row: a small icon saying whose words these are, and the sentence itself - a
 * line of prose rather than a note above a form control.
 *
 * `label` is optional, and the two remarks in this column differ on it. The construct's are the
 * ones you read on nearly every row, so they go unlabelled and let the icon and the grey say
 * whose they are; the ABHI Remarks are the rarer line and the one that can be acted on, so they
 * are named. `title` is the tooltip's label either way - a truncated sentence needs saying whose
 * it is even where the line itself does not.
 *
 * Nothing written is nothing shown - a label with a dash after it takes a row's worth of width
 * to say that there is nothing to read. It follows that a line is never empty, so there is
 * always a sentence for the tooltip to hold and always something for a click to edit.
 *
 * The line is prose, so it is truncated to the row's width and carries the whole thing in its
 * tooltip. `onEdit` is what makes it editable - the construct's own words are never given one,
 * and neither is anything on a read-only user's screen.
 */
const RemarkLine = ({icon, label, title, text, colour, onEdit}) => {
    const shown = String(text || '').trim();
    if (!shown) {
        return null;
    }
    const named = `${title || label}: ${shown}`;
    return (
        <Tooltip placement="top-start" title={onEdit ? `${named} — click to edit` : named}>
            <Box sx={onEdit ? editableRemarkLineSx : remarkLineSx} onClick={onEdit}>
                <Box style={{
                    display: 'flex',
                    flexShrink: 0,
                    color: colour,
                    paddingTop: '2px',
                    opacity: 0.85,
                }}>
                    {icon}
                </Box>
                <Typography style={{...oneLineStyle, fontSize: '0.72rem', color: colour, minWidth: 0}}>
                    {label && <b style={{fontWeight: 700}}>{label}: </b>}
                    {shown}
                </Typography>
            </Box>
        </Tooltip>
    );
};

/**
 * Every change to a line item here is a person overruling the Procedure Construct, and an
 * overruling with no reason written on it cannot be reviewed by anyone downstream. So the
 * reason is taken first and the change lands with it: one commit carrying both the new figures
 * and the remark, so no row can reach the save payload having been changed silently.
 *
 * The remark is written to the row's own `remarks` - the ABHI Remarks the tariff grid renders -
 * and not to the construct's remarks, which are the construct's own words and nobody else's.
 */
const CHANGE_KINDS = {
    payable: {
        title: 'Make this line item payable again',
        note: 'The Procedure Construct is not paying this line item. Say why it should be paid'
            + ' after all - the reason is written to its ABHI Remarks.',
        confirm: 'Make payable',
        // What the same dialog says when the change is being made to a checked set rather than
        // to one row. One reason covers the set - which is the honest shape of the decision:
        // somebody looked at those line items together and made one call about them.
        bulk: {
            title: (count) => `Make ${count} line items payable again`,
            note: 'The Procedure Construct is not paying these line items. Say why they should be'
                + ' paid after all - the reason is written to the ABHI Remarks of every one of'
                + ' them.',
            confirm: (count) => `Make ${count} payable`,
        },
    },
    disallow: {
        title: 'Disallow this line item',
        note: 'This line item is payable as it stands. Say why it should not be paid - the'
            + ' reason is written to its ABHI Remarks.',
        confirm: 'Disallow',
        bulk: {
            title: (count) => `Disallow ${count} line items`,
            note: 'These line items are payable as they stand. Say why they should not be paid -'
                + ' the reason is written to the ABHI Remarks of every one of them.',
            confirm: (count) => `Disallow ${count}`,
        },
    },
    quantity: {
        title: 'Change the non-payable quantity',
        note: 'Say why this many units should not be paid - the reason is written to the line'
            + " item's ABHI Remarks.",
        confirm: 'Change quantity',
    },
    clear: {
        title: 'Put the non-payable quantity back',
        note: 'The quantity goes back to the figure this line item arrived with. Say why - the'
            + " reason is written to the line item's ABHI Remarks.",
        confirm: 'Put back',
    },
    // The one kind that changes no figure. It still goes through this dialog rather than being
    // typed on the row, so a remark is written the same way here however it is reached.
    remark: {
        title: 'Edit the ABHI Remarks',
        note: 'The note this line item carries into the tariff grid and the save payload. The'
            + " construct's own remarks are not touched - those are its words, not anybody's to"
            + ' rewrite.',
        confirm: 'Save remark',
    },
};

// The dialog is dressed as the workspace it opens over: the same header band, the same badge,
// the same rules and blues - so it reads as this panel asking a question, not as a stray alert.
const changeDialogPaperSx = {
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 24px 60px rgba(18, 49, 91, 0.22)',
};

const changeDialogHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '13px 16px',
    background: 'linear-gradient(90deg, #e9f1fb 0%, #f7fbff 55%, #edf5ff 100%)',
    borderBottom: '1px solid #dce7f4',
};

const changeDialogBadgeStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '34px',
    height: '34px',
    flexShrink: 0,
    borderRadius: '10px',
    color: '#ffffff',
    background: 'linear-gradient(135deg, #6d5ce7 0%, #4a3fd0 100%)',
    boxShadow: '0 2px 6px rgba(74, 63, 208, 0.35)',
};

const changeDialogCloseStyle = {
    width: '30px',
    height: '30px',
    flexShrink: 0,
    borderRadius: '8px',
    color: '#5c7086',
    background: '#ffffff',
    border: '1px solid #dce7f4',
};

// The label sits above the box rather than floating into its outline: one plain input with a
// heading, which is what every other figure in this panel is labelled like.
const changeDialogLabelStyle = {
    fontSize: '0.6rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#8195ad',
};

/**
 * A plain input, and a focus state you can see.
 *
 * The ring is a halo rather than a thicker rule: MUI's own focused state swaps the 1px outline
 * for a 2px one, which nudges the text inside it by a pixel the moment the box is reached for.
 * Holding the border at 1px and putting the emphasis in a box-shadow keeps the box still.
 */
const changeDialogInputSx = {
    background: '#ffffff',
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        transition: 'box-shadow 130ms ease, border-color 130ms ease',
    },
    '& .MuiOutlinedInput-input': {padding: '10px 12px', fontSize: '0.82rem'},
    '& .MuiOutlinedInput-notchedOutline': {borderColor: '#dce7f4'},
    '&:hover .MuiOutlinedInput-notchedOutline': {borderColor: '#9fbcdd'},
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {borderColor: '#6d5ce7', borderWidth: '1px'},
    '& .MuiOutlinedInput-root.Mui-focused': {boxShadow: '0 0 0 3px rgba(109, 92, 231, 0.18)'},
    '& .Mui-error .MuiOutlinedInput-notchedOutline': {borderColor: '#d9534f', borderWidth: '1px'},
    '& .MuiOutlinedInput-root.Mui-error.Mui-focused': {boxShadow: '0 0 0 3px rgba(217, 83, 79, 0.16)'},
};

const changeDialogCancelSx = {
    textTransform: 'none',
    borderRadius: '9px',
    padding: '5px 14px',
    fontSize: '0.78rem',
    fontWeight: 600,
    color: '#5c7086',
    '&:hover': {background: '#f1f5fa'},
};

const changeDialogConfirmSx = {
    textTransform: 'none',
    borderRadius: '9px',
    padding: '5px 16px',
    fontSize: '0.78rem',
    fontWeight: 700,
    color: '#ffffff',
    background: 'linear-gradient(135deg, #6d5ce7 0%, #4a3fd0 100%)',
    boxShadow: '0 3px 10px rgba(74, 63, 208, 0.30)',
    '&:hover': {
        background: 'linear-gradient(135deg, #6151d8 0%, #3f35bb 100%)',
        boxShadow: '0 4px 12px rgba(74, 63, 208, 0.38)',
    },
    '&.Mui-disabled': {background: '#dfe6f0', color: '#a8b6c8', boxShadow: 'none'},
};

/**
 * The remark a change is waiting on. It opens on whatever remark the row already carries, so
 * amending a reason is not retyping it, and it will not close on an empty box: a change nobody
 * gave a reason for is a change that does not happen.
 *
 * The box is a normal single-line input, focused as the dialog settles. Two things follow from
 * it being one line: Enter is free to mean "save" rather than "newline", and the caret is put
 * at the end of whatever remark is already there, so amending one is typing rather than
 * clicking past it.
 *
 * The required error is held back until the box has been used - typed in, left, or Enter
 * pressed on it empty. Opening a dialog already showing red is scolding somebody for something
 * they have not had a chance to do yet.
 */
function ChangeRemarkDialog({pending, onCancel, onConfirm}) {
    const [text, setText] = React.useState('');
    const [touched, setTouched] = React.useState(false);
    const inputRef = React.useRef(null);

    // A set of rows carries no one remark to amend - they each have their own, and picking one of
    // them to show would be picking one row's words to put on all of them. So a bulk change opens
    // empty and a single change opens on what that row already says.
    const bulkItems = pending && Array.isArray(pending.items) && pending.items.length > 1
        ? pending.items
        : null;

    React.useEffect(() => {
        setText(pending && !(Array.isArray(pending.items) && pending.items.length > 1)
            ? get(pending, ['item', 'remarks'], '')
            : '');
        setTouched(false);
    }, [pending]);

    const kind = pending ? CHANGE_KINDS[pending.kind] : null;
    const bulk = bulkItems && kind ? kind.bulk : null;
    const count = bulkItems ? bulkItems.length : 0;
    // The names, where they fit - a count alone does not tell you what you have checked. Two
    // names and a tally reads at a glance and cannot grow past the line it is on.
    const subject = bulkItems
        ? bulkItems.slice(0, 2).map((item) => item.bill_line_item).join(', ')
        + (count > 2 ? ` and ${count - 2} more` : '')
        : pending ? get(pending, ['item', 'bill_line_item'], '') : '';
    const remark = String(text).trim();
    const showError = touched && remark.length === 0;

    /**
     * Focus on the way in. It is hung off the transition rather than `autoFocus` because the
     * dialog animates: autoFocus fires while the paper is still scaling up, and a browser will
     * not always keep focus on an element that is mid-transform.
     */
    const focusRemark = () => {
        const input = inputRef.current;
        if (!input) {
            return;
        }
        input.focus();
        const end = String(input.value).length;
        input.setSelectionRange(end, end);
    };

    const submit = () => {
        if (remark.length === 0) {
            setTouched(true);
            focusRemark();
            return;
        }
        onConfirm(remark);
    };

    return (
        <Dialog
            open={Boolean(pending)}
            onClose={onCancel}
            maxWidth="xs"
            fullWidth
            TransitionProps={{onEntered: focusRemark}}
            PaperProps={{sx: changeDialogPaperSx}}
        >
            <Box style={changeDialogHeaderStyle}>
                <Box style={changeDialogBadgeStyle}>
                    <EditNoteIcon style={{fontSize: '1.2rem'}}/>
                </Box>
                <Box style={{minWidth: 0, flexGrow: 1}}>
                    <Typography style={{
                        fontSize: '0.92rem',
                        fontWeight: 700,
                        color: '#12315b',
                        lineHeight: 1.25,
                    }}>
                        {bulk ? bulk.title(count) : kind ? kind.title : ''}
                    </Typography>
                    <Typography style={{...oneLineStyle, fontSize: '0.72rem', color: '#6b7f99'}}>
                        {subject}
                    </Typography>
                </Box>
                <IconButton size="small" aria-label="Cancel" onClick={onCancel} style={changeDialogCloseStyle}>
                    <CloseIcon style={{fontSize: '0.9rem'}}/>
                </IconButton>
            </Box>

            <DialogContent style={{padding: '15px 16px 4px'}}>
                <Typography style={{fontSize: '0.75rem', lineHeight: 1.5, color: '#63788f'}}>
                    {bulk ? bulk.note : kind ? kind.note : ''}
                </Typography>
                <Typography style={{...changeDialogLabelStyle, display: 'block', padding: '14px 0 5px'}}>
                    ABHI Remarks
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    inputRef={inputRef}
                    placeholder="Why is this changing?"
                    value={text}
                    error={showError}
                    onChange={(e) => setText(e.target.value)}
                    onBlur={() => setTouched(true)}
                    onKeyDown={(e) => {
                        // One line, so Enter cannot mean "newline" here - it means save.
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            submit();
                        }
                    }}
                    sx={changeDialogInputSx}
                    inputProps={{'aria-label': 'ABHI Remarks', maxLength: 500}}
                />
                <Typography style={{
                    fontSize: '0.68rem',
                    minHeight: '1.1rem',
                    padding: '6px 2px 0',
                    color: showError ? '#c62828' : '#8195ad',
                }}>
                    {showError
                        ? 'A remark is required to make this change.'
                        : 'Enter to save · Esc to cancel'}
                </Typography>
            </DialogContent>

            <DialogActions style={{padding: '10px 16px 15px', gap: '8px'}}>
                <Button onClick={onCancel} sx={changeDialogCancelSx}>Cancel</Button>
                <Button
                    variant="contained"
                    disableElevation
                    disabled={remark.length === 0}
                    onClick={submit}
                    sx={changeDialogConfirmSx}
                >
                    {bulk ? bulk.confirm(count) : kind ? kind.confirm : ''}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/**
 * The construct, over the panel. It is a wide read - eight-column tables, and clusters whose
 * remarks are full sentences - so it takes nearly the whole window rather than sharing the
 * decision column with the two bill lists.
 *
 * Its sections scroll inside the dialog, so opening one to follow a cluster back to the driver
 * that allowed it never moves the panel underneath.
 */
function ConstructDialog({open, onClose, construct, deduction}) {
    return (
        <Dialog
            open={Boolean(open)}
            onClose={onClose}
            maxWidth={false}
            PaperProps={{sx: constructDialogPaperSx}}
        >
            <Box style={changeDialogHeaderStyle}>
                <Box style={constructBadgeStyle}>
                    <AccountTreeIcon style={{fontSize: '1.15rem'}}/>
                </Box>
                <Box style={{minWidth: 0, flexGrow: 1}}>
                    <Typography style={{
                        fontSize: '0.92rem',
                        fontWeight: 700,
                        color: '#12315b',
                        lineHeight: 1.25,
                    }}>
                        Medical Construct
                    </Typography>
                    <Typography style={{fontSize: '0.72rem', color: '#6b7f99'}}>
                        How the Procedure Construct read this claim — the drivers it found, what
                        they allow, and every bill line against those allowances
                    </Typography>
                </Box>
                <IconButton size="small" aria-label="Close" onClick={onClose} style={changeDialogCloseStyle}>
                    <CloseIcon style={{fontSize: '0.9rem'}}/>
                </IconButton>
            </Box>

            <DialogContent style={{padding: '12px 14px 16px', background: '#fbfcfe'}}>
                <DynamicConstruct construct={construct} deduction={deduction}/>
            </DialogContent>
        </Dialog>
    );
}

function PcNonPayables(props) {
    const {
        claimId,
        tariffData,
        dynamicConstruct,
        pcSavingRealized,
        claimBillAmountRequested,
        documentFiles,
        onCommit,
        onAdjudicate,
        isLoading,
        attention,
        attentionTitle,
        attentionMessage,
    } = props;

    const listHeight = listMaxHeight(PANEL_CHROME_HEIGHT);

    /** Whose decision this is. An insurer read-only user reads the panel and writes nothing. */
    const readOnly = !isNotInsurerReadOnlyUser();
    /**
     * `pcSavingRealized` - the API's own `pc_updated` - used to be in here, and a claim that had
     * already recorded a Procedure Construct saving arrived frozen: every control dead and Save
     * & Adjudicate unpressable. It no longer is. A realised saving is a fact about the claim, not
     * a lock on it: the construct's findings can be revisited, a quantity corrected, and the
     * sheet saved again, and a second press writes the table as it then reads. The flag is still
     * read - it is what the button and its tooltip say has happened before - but it decides
     * nothing here.
     *
     * Permission and a load in flight still do: one is not this user's decision to take, and the
     * other would be a decision taken against rows that are about to be replaced.
     */
    const disabled = readOnly || Boolean(isLoading);

    // The change that has been asked for and is waiting on its reason, and a counter the
    // quantity boxes watch so that an abandoned one puts its figure back.
    const [pendingChange, setPendingChange] = React.useState(null);
    const [changeRevision, setChangeRevision] = React.useState(0);

    const [open, setOpen] = React.useState(false);
    // The construct is read beside the decision, not inside it - it opens over the panel and
    // closes back to exactly the panel that was there.
    const [constructOpen, setConstructOpen] = React.useState(false);
    const [search, setSearch] = React.useState('');
    /**
     * The IRDAI list arrives folded away. It is the one list on the panel with nothing to be
     * done to it, so it opens on being asked for rather than taking height from the two lists
     * that carry the decision. Opened by hand it stays open for the rest of the session: a
     * reader who wants it wants it on the next claim too, and nothing is riding on it being
     * shut. A search that hits it opens it as well - see the search box.
     */
    const [irdaiOpen, setIrdaiOpen] = React.useState(false);
    /**
     * What is checked in each of the two editable tables, by row key.
     *
     * Two sets and not one, because the two tables are the two ends of one move and a key checked
     * in one means the opposite of the same key checked in the other. They are kept as keys
     * rather than as rows so that nothing here can go stale against `rows`: a key whose line item
     * has moved tables, or left the sheet, simply stops matching anything - the selection is
     * resolved against the live lists on every render, never trusted on its own.
     */
    const [checkedBillKeys, setCheckedBillKeys] = React.useState(() => new Set());
    const [checkedCandidateKeys, setCheckedCandidateKeys] = React.useState(() => new Set());
    // The claim's saving as this panel first found it, so the session's own delta can be shown.
    const [existingSaving, setExistingSaving] = React.useState(null);
    // Sort and scroll survive the popup closing and reopening.
    const [sorting, setSorting] = React.useState({field: 'gain', direction: 'desc'});

    const baselinesRef = React.useRef(new Map());
    const scrollTopRef = React.useRef(0);
    const scrollTimerRef = React.useRef(null);
    const containerRef = React.useRef(null);

    const rows = React.useMemo(() => (Array.isArray(tariffData) ? tariffData : []), [tariffData]);

    /**
     * What the row arrived carrying. Taken once, on first sight, and before this panel has had
     * any chance to write to it - that snapshot is what a pull-back restores from.
     */
    const ensureBaseline = React.useCallback((row) => {
        const key = rowKey(row);
        if (key === undefined || key === null) {
            return null;
        }
        if (!baselinesRef.current.has(key)) {
            baselinesRef.current.set(key, baselineOf(row));
        }
        return baselinesRef.current.get(key);
    }, []);

    // Both figures come off the same tariff list, so rows present means the totals are populated.
    // Counted the same way `standingSaving` is - the construct's rows only - so the session
    // delta below is a difference between two figures of the same kind.
    React.useEffect(() => {
        if (existingSaving === null && rows.length > 0) {
            setExistingSaving(roundMoney(rows.reduce(
                (total, row) => total + (isPcBillLineItem(row) ? savingOf(row) : 0), 0)));
        }
    }, [rows, existingSaving]);

    React.useEffect(() => () => {
        if (scrollTimerRef.current) {
            clearTimeout(scrollTimerRef.current);
        }
    }, []);

    /** A payable row, priced for the decision that has not been taken on it yet. */
    const describeCandidate = React.useCallback((row) => {
        const key = rowKey(row);
        ensureBaseline(row);
        const nonPayableUnits = effectiveQuantity(row);
        const charged = chargedQuantity(row);
        const payableUnits = Math.max(charged - nonPayableUnits, 0);
        return {
            key,
            row,
            charged,
            nonPayableUnits,
            payableUnits,
            billAmount: billAmountOf(row),
            projected: pcNonPayableAmounts(row, nonPayableUnits),
            gain: addedSavingIfMarked(row, nonPayableUnits),
            isCut: isPartialCut(charged, payableUnits),
            bill_line_item: String(get(row, ['bill_line_item'], '')),
            constructRemarks: constructRemarksOf(row),
            remarks: remarksOf(row),
        };
    }, [ensureBaseline]);

    /**
     * ...and a decided row, read off as it stands - with one exception, its saving, which is
     * priced from the row's own non-payable quantity rather than read off `savings`.
     *
     * `savings` only moves when a decision lands, and adjusting a quantity in this table is not
     * a decision landing: `stagePayableQuantity` writes the units and the verdict and leaves
     * the money alone. So the stored figure goes stale the moment the Payable Qty box is
     * touched, and a row that arrived disallowed carrying no figure at all would print nothing.
     * Pricing it here keeps the Saving column answering the quantity beside it.
     *
     * This is display only. Nothing is written back to the row, so the claim's own Saving total
     * and the save payload still stand on exactly what the rows carry.
     */
    const describeBillLineItem = React.useCallback((row) => {
        const baseline = ensureBaseline(row);
        const charged = chargedQuantity(row);
        // The figure the commit will use, not just the one the row carries: a line item the
        // construct vetoed and nobody has touched commits whole, so the column has to say so
        // before the button is pressed rather than after.
        const nonPayableUnits = committedNonPayableQuantity(row);
        const payableUnits = Math.max(charged - nonPayableUnits, 0);
        return {
            key: rowKey(row),
            row,
            charged,
            nonPayableUnits,
            payableUnits,
            billAmount: billAmountOf(row),
            saving: pcNonPayableAmounts(row, nonPayableUnits).savings,
            verdict: pcVerdictTextOf(row),
            isCut: isPartialCut(charged, payableUnits),
            exactRestore: arrivedPayable(baseline),
            /**
             * Was this row's saving already taken, before the panel was opened? It arrived
             * carrying "Non-Payable by Procedure Construct" and still carries it, so nothing
             * about it is being decided now and its money is already on the claim.
             *
             * Both halves are asked. The arrival alone would keep calling a row done after
             * somebody re-cut it to a partial; the current verdict alone would call a row done
             * the moment a quantity box was filled to the whole line item, and that decision has
             * not been saved yet - it would drop out of the figure that is about to save it, and
             * take the save button with it.
             */
            alreadyDecided: arrivedNonPayable(baseline) && isPcNonPayable(row),
            bill_line_item: String(get(row, ['bill_line_item'], '')),
            constructRemarks: constructRemarksOf(row),
            remarks: remarksOf(row),
        };
    }, [ensureBaseline]);

    const sortItems = React.useCallback((items) => {
        const {field, direction} = sorting;
        const factor = direction === 'asc' ? 1 : -1;
        return [...items].sort((a, b) => {
            const left = a[field];
            const right = b[field];
            if (typeof left === 'string' || typeof right === 'string') {
                return String(left).localeCompare(String(right)) * factor;
            }
            return (left - right) * factor;
        });
    }, [sorting]);

    /**
     * Can a decision be taken on this row at all? Only a row this panel can address by key: a
     * change is committed by the row it is written onto, so one with no key has nothing to be
     * written back to.
     *
     * It used to turn IRDAI non-payables away here as well - a decision on a line IRDAI already
     * takes in full adds nothing. `pcRouteOf` now settles that before either list is built, by
     * asking `irdai_payable` first, so this is the key test and nothing else.
     */
    const isDecidable = React.useCallback((row) => {
        const key = rowKey(row);
        return key !== undefined && key !== null;
    }, []);

    /** The Bill Line Item table: everything the claim is not paying, as the rows now stand. */
    const billLineItems = React.useMemo(() => rows
        .filter(isPcBillLineItem)
        .map(describeBillLineItem),
        [rows, describeBillLineItem]);

    /**
     * The Disallowed table: every row still open to a decision, whatever disallowing it is worth.
     *
     * Nothing is filtered on money, on the same grounds the IRDAI list below is not. A line item
     * whose bill is zero or negative - a credit note, a reversal, one written off - is still a
     * line item on the tariff sheet, and a row sitting in the grid while this screen showed no
     * trace of it was the harder thing to account for. The three lists show the sheet whole.
     *
     * What a row is worth is said in its own Add column instead, where a decision that adds
     * nothing reads as nothing rather than as a row that does not exist. `searchedCandidates`
     * sorts by that figure descending, so the rows worth pressing stay at the top of the list.
     */
    const candidateItems = React.useMemo(() => rows
        .filter((row) => isDecidable(row) && isPcDisallowed(row))
        .map(describeCandidate),
        [rows, isDecidable, describeCandidate]);

    /**
     * The IRDAI list: every line item IRDAI does not pay.
     *
     * No subtraction and no second thought about the two lists above it - `pcRouteOf` asks
     * `irdai_payable` before it asks anything else, so a row that lands here cannot also be in
     * either of them. The three lists partition the tariff sheet by construction, which is what
     * keeps one line item's amount from being read, or totalled, twice on one screen.
     *
     * Nothing is filtered on money: an IRDAI row is on the claim whether or not it carries a
     * figure, and a list that quietly dropped the ones carrying none would be the same missing
     * rows this list exists to end.
     */
    const irdaiItems = React.useMemo(() => rows
        .filter(isIrdaiListItem)
        .map(describeIrdaiItem)
        .filter((item) => item.key !== undefined && item.key !== null),
        [rows]);

    /**
     * What IRDAI is already not paying, across that list. Its own figure, kept beside the
     * Procedure Construct's rather than folded into it: this money is off the claim before the
     * construct is consulted, so counting it as a Procedure Construct saving would credit this
     * panel with a deduction it had no part in - and would leave the Saving card reading
     * differently from the table it is totalling, and from what the save button writes.
     */
    const irdaiNonPayableTotal = React.useMemo(
        () => roundMoney(irdaiItems.reduce((total, item) => total + item.amount, 0)),
        [irdaiItems]);

    const searchNeedle = search.trim().toLowerCase();

    const listItems = React.useMemo(
        () => sortItems(billLineItems.filter((item) => matchesSearch(item, searchNeedle))),
        [billLineItems, searchNeedle, sortItems]);

    /**
     * The one search box drives all three lists, so a line item hunted for is found wherever it
     * turns out to be sitting. Sorted by what the claim is not paying on it, largest first -
     * there is no decision to take here, so the only useful order is by size.
     */
    const searchedIrdaiItems = React.useMemo(
        () => [...irdaiItems.filter((item) => matchesSearch(item, searchNeedle))]
            .sort((a, b) => b.amount - a.amount),
        [irdaiItems, searchNeedle]);

    const searchedCandidates = React.useMemo(
        () => [...candidateItems.filter((item) => matchesSearch(item, searchNeedle))]
            .sort((a, b) => b.gain - a.gain),
        [candidateItems, searchNeedle]);

    /**
     * The checked rows, resolved against what each table is actually showing.
     *
     * Resolved rather than remembered, and against the *searched* list rather than the whole one:
     * a check is on a row you can see, so a needle typed after checking narrows the set the bulk
     * button acts on, and clearing the needle brings the rest of it back. The button says how many
     * it is about to move, and the rows it means are the rows on screen with a tick against them -
     * nothing waiting off-list to be swept up with them.
     *
     * It follows that a row that has moved tables, or left the sheet, drops out on its own: its
     * key matches nothing here, so no stale key can ever be handed to a change.
     */
    const checkedBillItems = React.useMemo(
        () => listItems.filter((item) => checkedBillKeys.has(item.key)),
        [listItems, checkedBillKeys]);

    const checkedCandidates = React.useMemo(
        () => searchedCandidates.filter((item) => checkedCandidateKeys.has(item.key)),
        [searchedCandidates, checkedCandidateKeys]);

    /**
     * What the claim would save if every line item in the Disallowed pool were added - the
     * headline figure on the button that opens this panel, and nothing more than that. It is an
     * offer, not something a commit acts on: no button disallows the pool wholesale, so the
     * figure is only ever collected one Add at a time.
     */
    const markAllAddedSaving = React.useMemo(
        () => roundMoney(candidateItems.reduce(
            (total, item) => total + (item.gain > MONEY_EPSILON ? item.gain : 0), 0)),
        [candidateItems]);

    /**
     * The Bill Line Item table, split by whether its money is still to be saved.
     *
     *   decided   it arrived "Non-Payable by Procedure Construct" and still reads that way. The
     *             claim has been carrying its saving since before this panel was opened; there is
     *             nothing here to decide and nothing to press for.
     *   pending   everything else in the table - a line item the construct vetoed and whose
     *             verdict still reads "Payable by Procedure Construct", or one somebody has cut
     *             by hand. This is the work, and this is the money the press will add.
     *
     * Splitting them is the whole point: a table of nothing but already-saved rows was printing
     * their total as though pressing Save would earn it again.
     */
    const decidedItems = React.useMemo(
        () => billLineItems.filter((item) => item.alreadyDecided), [billLineItems]);

    const pendingItems = React.useMemo(
        () => billLineItems.filter((item) => !item.alreadyDecided), [billLineItems]);

    /**
     * The one figure this panel prints, everywhere it prints one: the collapsed button's
     * headline, the Saving card, the strip above the table, and the save button's own note. The
     * pending half of the Bill Line Item table's Saving column, added up - so none of them can
     * ever read differently from the rows they are all totalling. It moves with the quantities
     * because the column does.
     *
     * It is also what pressing Save & Adjudicate will add. The commit prices every row in the
     * table from `applyPcNonPayable(row, item.nonPayableUnits)` - the same term summed here, at
     * the same units - but a decided row is repriced to exactly the figure it already carries, so
     * what the press *changes* is this sum and no more. The button's note is a promise it keeps.
     *
     * Not to be confused with `standingSaving` below - that one is read off every row's stored
     * `savings`, which is what the rows carry now rather than what the commit would write: stale
     * wherever a Payable Qty box has been touched, and nothing at all for a row that arrived
     * disallowed carrying no figure. It is kept for the session delta alone, where it is compared
     * against a stored figure of its own kind.
     */
    const disallowedSaving = React.useMemo(
        () => roundMoney(pendingItems.reduce((total, item) => total + item.saving, 0)),
        [pendingItems]);

    /**
     * ...and what the claim already holds from the decided half. Printed beside the figure above
     * rather than added to it, and never counted as something this press will earn: it is the
     * answer to "then why does the table total more than the card".
     */
    const decidedSaving = React.useMemo(
        () => roundMoney(decidedItems.reduce((total, item) => total + item.saving, 0)),
        [decidedItems]);

    /**
     * What the construct's own findings are worth, in money: every unit it did not allow, at
     * what the bill charged for that unit. The card in the construct view reads this.
     *
     * An estimate, and deliberately not `standingSaving`. That one moves with every decision
     * taken by hand in this panel, and includes cuts the construct never asked for. This one
     * answers a narrower question - what did the construct itself find - and so it is read off
     * `construct_object` alone. A line item the construct never ruled on contributes nothing
     * rather than its whole bill, which is why the units are checked before the row is priced:
     * a claim whose rows carry no `construct_object` deducts nothing here.
     *
     * IRDAI's own non-payables are skipped, on the same precedence the lists are built on: that
     * money is off the claim before the construct is consulted, so counting a construct ruling
     * on top of it would total one line item's bill twice - once here and once in the IRDAI
     * figure - and credit the construct with a deduction IRDAI had already taken.
     */
    const constructDeduction = React.useMemo(() => roundMoney(rows.reduce((total, row) => {
        if (isIrdaiListItem(row)) {
            return total;
        }
        const units = constructObjectNonPayableQuantity(row);
        return units > 0 ? total + pcNonPayableAmounts(row, units).pcSaving : total;
    }, 0)), [rows]);

    /**
     * What the Procedure Construct saves as it stands. Read straight off the rows rather than
     * tallied as decisions are taken, so disallowing and pulling back can never leave the figure
     * adrift.
     *
     * Only rows the construct is not paying are counted - the Non Payable / Partially Payable
     * list, `isPcBillLineItem`. A row's `savings` is the whole claim's saving on that line and
     * carries the tariff's own reduction with it, so summing every row would credit this panel
     * with money the Procedure Construct had no part in: a claim whose rows all come back
     * payable, with `construct_object` null throughout, would still print a saving here. The
     * tariff sheet is where that figure belongs. Nothing disallowed is nothing saved.
     *
     * One reader left: `sessionAddedSaving`, the footer's "against what it was when this panel
     * was opened". That comparison is the whole reason this figure is still derived - both sides
     * of it are stored `savings`, so the delta is between two figures of one kind. Nothing the
     * panel prints as its saving comes through here any more; `disallowedSaving` is that, on the
     * grounds set out above it.
     */
    const pcSavingOf = React.useCallback(
        (row) => (isPcBillLineItem(row) ? savingOf(row) : 0), []);

    const standingSaving = React.useMemo(
        () => roundMoney(rows.reduce((total, row) => total + pcSavingOf(row), 0)),
        [rows, pcSavingOf]);

    const claimTotal = React.useMemo(() => {
        const total = parseFloat(claimBillAmountRequested);
        return Number.isFinite(total) && total > 0 ? total : 0;
    }, [claimBillAmountRequested]);

    const sessionAddedSaving = roundMoney(standingSaving - (existingSaving === null ? standingSaving : existingSaving));
    const markAllSavingPercent = claimTotal > 0 ? (markAllAddedSaving / claimTotal) * 100 : 0;
    const disallowedSavingPercent = claimTotal > 0 ? (disallowedSaving / claimTotal) * 100 : 0;

    /**
     * Is there a saving here for the press to add? Two things have to be true:
     *
     *   a row to decide   at least one line item in the Bill Line Item table whose saving is not
     *                     already on the claim - typically one the construct vetoed whose verdict
     *                     still reads "Payable by Procedure Construct", or one cut by hand. A
     *                     table holding nothing but rows that arrived "Non-Payable by Procedure
     *                     Construct" is a table of work already done.
     *   money in it       and that half has to be worth something: above 0% of the claim where
     *                     `claimBillAmountRequested` came through to divide by, and above a rupee
     *                     where it did not. The two are the same test - the percentage is this
     *                     amount over the claim - so the amount is what is asked, and it is asked
     *                     once rather than differently in each place a figure is printed.
     *
     * This is the figure question, and it is what the headline and the button's note read. It is
     * not on its own what the button asks - see below.
     */
    const noSavingToAdd = pendingItems.length === 0 || disallowedSaving <= MONEY_EPSILON;

    /**
     * ...and the other reason to press: something in this panel has been changed and the change
     * is sitting in the tariff list unsaved.
     *
     * A saving is not the only thing a press carries. Pressing x on a line item takes money
     * *off* the claim - it lowers the saving, by design - and a quantity corrected downwards does
     * the same. Judged on the figure alone the button would go dead exactly when the reader had
     * just done the one thing that needs saving most, and the correction would sit staged until
     * somebody thought to press Save Tariff instead. So any change the panel commits arms it:
     * a quantity settled, a box cleared, an Add, an x, a remark. All of them write rows.
     *
     * It is cleared by the press that saves them, and by nothing else - a change is unsaved until
     * it has actually gone out.
     */
    const [hasStagedChange, setHasStagedChange] = React.useState(false);

    /**
     * So this is what the one green button asks: is there a saving to add, or is there a change
     * to save? Either will do.
     *
     * Only this button. `disabled` still gates every control in the panel - permission and a load
     * in flight - and neither of those is what this adds; a live table with nothing yet to commit
     * stays live, it just has nothing to commit.
     */
    const saveDisabled = disabled || (noSavingToAdd && !hasStagedChange);

    /**
     * The same figure, projected: what the table would total if every payable line item left in
     * the pool were added. Built on `disallowedSaving` rather than on `standingSaving`, because
     * that is the figure the card prints - a projection has to add up in front of the reader,
     * and one starting from a base the card never showed does not.
     *
     * The addend is each candidate's gain, not its whole projected saving, so the tariff
     * reduction a payable row already carries is not advertised here as new money. That saving
     * is real, it is just the tariff sheet's, which is the line this panel holds everywhere else.
     */
    const revisedSaving = roundMoney(disallowedSaving + markAllAddedSaving);
    const revisedSavingPercent = claimTotal > 0 ? (revisedSaving / claimTotal) * 100 : 0;

    /**
     * The whole claim's non-payables, every rule put together - what the Procedure Construct is
     * about to stop paying, what it already stopped paying, and what IRDAI does not pay. All
     * three terms, because this is the only figure on the panel that answers "how much of this
     * claim is not being paid" rather than "what is this press worth": leaving the already-saved
     * half out of the headline is right, and leaving it out of the claim's own total would be a
     * different figure quietly gone missing.
     *
     * It is stated in words, in the IRDAI figure's own tooltip, and deliberately not printed as a
     * figure of its own: the amounts are saved by different rules and only one of them is this
     * panel's to move, so a single total on the band would be read as the amount the save button
     * acts on. The three lists partition the tariff sheet, so this adds each line item once.
     */
    const irdaiPercent = claimTotal > 0 ? (irdaiNonPayableTotal / claimTotal) * 100 : 0;
    const claimNotPaying = roundMoney(disallowedSaving + decidedSaving + irdaiNonPayableTotal);
    const claimNotPayingPercent = claimTotal > 0 ? (claimNotPaying / claimTotal) * 100 : 0;

    // The headline is the Non Payable / Partially Payable list's own total - what this claim is
    // actually not paying, as that list reads. Nothing in the list is nothing saved, however
    // much the Payable pool could still yield if every line item in it were added, so an empty
    // list gives way to the invitation to open the panel rather than printing a figure the
    // claim has not earned. A percentage needs claimBillAmountRequested to divide by; rupees
    // stand in when it has not arrived.
    const headline = noSavingToAdd
        ? 'Save More'
        : claimTotal > 0
            ? `Save ${formatPercent(disallowedSavingPercent)}%`
            : `Save ${formatRupees(disallowedSaving)}`;

    /**
     * What the save button says, and why. The figure it is about to add, where there is one -
     * and where there is not but something has been changed, the change is what it says, because
     * that is then the reason it will press. A claim that has already realised a saving is not a
     * special case on the button's face; the press is no longer refused, and what was already
     * recorded is said in the tooltip, before pressing rather than instead of pressing.
     */
    const saveNote = pendingItems.length > 0
        ? `${pendingItems.length} to disallow — ${formatRupees(disallowedSaving)}`
        + `${claimTotal > 0 ? ` (${formatPercent(disallowedSavingPercent)}%)` : ''}`
        : hasStagedChange
            ? 'Changes to save'
            : decidedItems.length > 0
                ? 'Already saved — nothing new to disallow'
                : 'Nothing disallowed';

    const saveTooltip = readOnly
        ? 'You do not have permission to save on this claim.'
        : noSavingToAdd && !hasStagedChange
            ? (decidedItems.length > 0
                ? `Every line item in the Bill Line Item table is already saved on this claim —`
                + ` ${formatRupees(decidedSaving)} across ${decidedItems.length} of them. There is`
                + ' nothing new to commit. Change something here — add a payable line item, cut a'
                + ' quantity, or make one payable again — and this will press.'
                : 'Nothing is disallowed on this claim, so there is nothing to save. Add a payable'
                + ' line item below to disallow it.')
            : 'Commit the Bill Line Item table as it reads and adjudicate the claim.'
            + (noSavingToAdd
                ? ' It adds no saving as it stands — what it saves is the change already made'
                + ' here, which is staged in the tariff list until something sends it.'
                : '')
            + (pcSavingRealized
                ? ' A Procedure Construct saving has already been recorded on this claim — saving'
                + ' again replaces it with the table as it now reads.'
                : '');

    /**
     * The construct button's own second line. It is there so the button can be judged without
     * being pressed: the length of stay the whole construct hangs on, and how many bill lines
     * it did not wave through.
     */
    const constructLos = get(dynamicConstruct, ['allowed_los']);
    const constructFlagged = ['disallowed_lines', 'capped_lines', 'review_lines']
        .reduce((total, key) => total + (get(dynamicConstruct, ['summary', key], 0) || 0), 0);
    const constructNote = !get(dynamicConstruct, ['applied'], false)
        ? 'Not applied to this claim'
        : [
            constructLos || constructLos === 0 ? `LOS ${constructLos}d` : null,
            `${constructFlagged} line(s) flagged`,
        ].filter(Boolean).join(' · ');

    /**
     * One card, not two. Saving and Disallowed were the same money read twice - what the claim
     * is not paying is what the claim saves - and two cards printing one figure side by side
     * invited the reader to hunt for a difference that is not there. So the band carries the
     * saving alone, and what the second card was actually adding - how many line item(s) that
     * figure is totalling - comes back as a chip beside it: the amount, its share of the claim,
     * and the count of rows behind it, read left to right off one card.
     *
     * `disallowedSaving`, and deliberately not `standingSaving`. The two are different questions
     * and the card is being read next to the save button, which answers the first: what will this
     * claim be saving once I press this. Printing the stored figure here left the card and the
     * button disagreeing by whatever had been typed into a Payable Qty box and never
     * committed - two amounts, one claim, no way to tell from the screen which one the press
     * would land. Both now read the table they are both totalling.
     *
     * And they read the half of it that is still to be saved. A row that arrived non-payable is
     * money the claim already has, so it is not in the amount; it is in a chip of its own beside
     * it, and its rows are tinted green in the table, because a figure that totals less than the
     * column under it has to say where the rest went.
     */
    const figures = [
        {
            label: 'Saving',
            value: formatRupees(disallowedSaving),
            chips: [
                claimTotal > 0 ? {text: `${formatPercent(disallowedSavingPercent)}%`, tone: 'money'} : null,
                pendingItems.length > 0
                    ? {text: `${pendingItems.length} item(s)`, tone: 'count'}
                    : null,
                // What the claim already holds, said in its own chip. Never added into the
                // amount: the card is what this press is worth, and that money is not.
                decidedSaving > MONEY_EPSILON
                    ? {text: `${formatRupees(decidedSaving)} saved`, tone: 'count'}
                    : null,
            ],
            tooltip: (pendingItems.length === 0
                ? (decidedItems.length > 0
                    ? `The Procedure Construct saving on this claim is already taken —`
                    + ` ${formatRupees(decidedSaving)} across ${decidedItems.length} line item(s)`
                    + ' that arrived non-payable. There is nothing new to save, so this figure is'
                    + ' nought rather than a second helping of money the claim already holds.'
                    : 'Nothing is disallowed by the Procedure Construct on this claim, so it saves'
                    + ' nothing here. A tariff reduction on a payable line item is the tariff'
                    + " sheet's own saving and is not counted in this panel.")
                : `What saving this press would add: ${pendingItems.length} line item(s) in the`
                + ' Bill Line Item table are not yet saved on the claim. Press x on one to make it'
                + ' payable again.'
                + (decidedSaving > MONEY_EPSILON
                    ? ` A further ${formatRupees(decidedSaving)} across ${decidedItems.length} line`
                    + ' item(s) is already saved and is not counted here.'
                    : ''))
                + (markAllAddedSaving > MONEY_EPSILON
                    ? ` Adding all ${candidateItems.length} payable line item(s) adds`
                    + ` ${formatRupees(markAllAddedSaving)}`
                    + `${claimTotal > 0 ? ` (${formatPercent(markAllSavingPercent)}%)` : ''}`
                    + ` — revised saving ${formatRupees(revisedSaving)}`
                    + `${claimTotal > 0 ? ` (${formatPercent(revisedSavingPercent)}%)` : ''}.`
                    : ' Nothing payable is left to disallow.'),
        },
        // Only where there is one. A pill reading zero across nought line items is a claim
        // with no IRDAI non-payables telling the reader about a rule it never met.
        irdaiItems.length > 0
            ? {
                label: 'IRDAI Non-Payable',
                value: formatRupees(irdaiNonPayableTotal),
                color: '#8a6d1f',
                chips: [
                    claimTotal > 0 ? {text: `${formatPercent(irdaiPercent)}%`, tone: 'money'} : null,
                    {text: `${irdaiItems.length} item(s)`, tone: 'count'},
                ],
                tooltip: `What IRDAI does not pay on this claim, across ${irdaiItems.length} line`
                    + ' item(s) — listed at the bottom of this panel, and already off the claim'
                    + ' before the Procedure Construct is consulted. Nothing here is this'
                    + " panel's to decide or to save, so it is kept out of the Saving beside it."
                    + ` Every rule together — this, the saving beside it, and`
                    + ` ${formatRupees(decidedSaving)} already saved — comes to`
                    + ` ${formatRupees(claimNotPaying)}`
                    + `${claimTotal > 0 ? ` (${formatPercent(claimNotPayingPercent)}%)` : ''}`
                    + ' the claim is not paying.',
            }
            : null,
    ].filter(Boolean);

    /**
     * Ask for the reason. `stage` is the change itself, held unrun until there is one: the row
     * it returns and the remark are written in a single commit, so the panel never holds a row
     * that has been changed without being explained.
     */
    const requestChange = (kind, item, stage, items) => {
        if (disabled) {
            return;
        }
        setPendingChange({kind, item, stage, items: items || [item]});
    };

    const confirmChange = (remark) => {
        if (!pendingChange) {
            return;
        }
        // One row or a set: `stage` may hand back either, and both leave here as a list carrying
        // the one reason that was written for them.
        const changed = pendingChange.stage();
        setPendingChange(null);
        const staged = (Array.isArray(changed) ? changed : [changed])
            .filter(Boolean)
            .map((row) => stageRemarks(row, remark));
        if (staged.length === 0) {
            return;
        }
        onCommit?.(staged);
        // The rows have moved, so no tick on any of them still means what it meant. Both tables
        // are cleared rather than pruned - a half-remembered selection across a move is worse to
        // read than a clean one.
        setCheckedBillKeys(new Set());
        setCheckedCandidateKeys(new Set());
        // Every change the panel takes comes through here - a quantity, a clear, an Add, an x, a
        // remark - so this is the one place the button has to be armed from, whatever the change
        // did to the figure.
        setHasStagedChange(true);
    };

    /**
     * Nothing is written, and the quantity boxes are told to reload from their rows - one of
     * them may be showing a figure that was typed for a change that never happened.
     */
    const cancelChange = () => {
        setPendingChange(null);
        setChangeRevision((revision) => revision + 1);
    };

    /**
     * A figure settled in the Payable column is a write to the row's own
     * `allowed_units_by_proc_construct` - the field that column is. Nothing is priced: all that
     * has been said is how far a decision would reach if one were taken, and the Saving beside it
     * follows the figure because the table reads it back off the row.
     *
     * With one exception, and it is the reason this is not a one-liner. Every charged unit
     * payable is not a smaller cut, it is no cut at all - the line item is payable again - so it
     * takes the x button's own path and the row pulls back down into the Disallowed pool.
     * Staging the units alone would not get it there:
     *
     *   the veto   `pcRouteOf` asks `construct_object.allowed` before it asks the verdict, so a
     *              line item the construct threw out would sit in this table reading 16 / 16 and
     *              claiming to be fully payable. `revertPcNonPayable` lifts the veto; staging
     *              does not, and should not - staging is not an overruling.
     *   the money  a staged row keeps the `savings` it was carrying. On a row that is now payable
     *              in full that figure is a saving on nothing, still counted, and the pull-back
     *              is what puts it back to what the row was found with.
     */
    const settleQuantity = (item, payableUnits) => {
        if (item.charged > 0 && payableUnits >= item.charged) {
            makeRowPayable(item);
            return;
        }
        requestChange('quantity', item, () => stagePayableQuantity(item.row, payableUnits));
    };

    /**
     * The ABHI Remarks on a row, edited. It writes the row's own `remarks` - the field the
     * tariff grid's Remarks column edits, so the note shows up in both places - and nothing
     * else, so `stage` hands the row back exactly as it found it and only the remark lands.
     */
    const editRemarks = (item) => {
        requestChange('remark', item, () => item.row);
    };

    /**
     * An emptied box puts the figure back to whatever the row was found carrying - and then takes
     * whichever of the two paths that figure means, on the same rule `settleQuantity` uses. A box
     * cleared to the whole line item and a box typed to the whole line item say the same thing, so
     * they do the same thing: the row pulls back down into the Disallowed pool.
     */
    const clearQuantity = (item) => {
        const restored = restoredPayableQuantity(item.row, baselinesRef.current.get(item.key));
        if (item.charged > 0 && restored >= item.charged) {
            makeRowPayable(item);
            return;
        }
        requestChange('clear', item, () => stagePayableQuantity(item.row, restored));
    };

    /**
     * Disallow one line item, from the Disallowed pool: the flag goes false and the row is
     * priced for the units the construct leaves unallowed. It moves up into the Bill Line Item
     * table on the next render, because that is where the flag now puts it.
     */
    const disallowRow = (item) => {
        requestChange('disallow', item, () => applyPcNonPayable(item.row, item.nonPayableUnits));
    };

    /** One tick, on or off. */
    const toggleChecked = (setKeys) => (key) => {
        setKeys((previous) => {
            const next = new Set(previous);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const toggleBillChecked = toggleChecked(setCheckedBillKeys);
    const toggleCandidateChecked = toggleChecked(setCheckedCandidateKeys);

    /**
     * The header tick: all of what is on screen, or none of it.
     *
     * It writes only the keys the list is currently showing and leaves any others alone, so
     * ticking the header under a search does not quietly take in everything the needle hid. Its
     * own state is read the same way - see `allChecked` / `someChecked` at the two headers.
     */
    const toggleAll = (setKeys, items) => (checkAll) => {
        setKeys((previous) => {
            const next = new Set(previous);
            items.forEach((item) => (checkAll ? next.add(item.key) : next.delete(item.key)));
            return next;
        });
    };

    /**
     * ...and the two moves, made to everything ticked at once.
     *
     * They are the single-row moves, over a list: the same `revertPcNonPayable` and the same
     * `applyPcNonPayable` at the same units, so a set of five behaves exactly as five presses
     * would have, and one reason is written across all of them. Nothing new is decided here -
     * only how many rows one decision covers.
     *
     * The ticks are dropped when the change lands, not when it is asked for: a reason abandoned
     * at the dialog leaves the set exactly as it was, so cancelling costs nothing but the typing.
     */
    const makeCheckedPayable = () => {
        const items = checkedBillItems;
        if (items.length === 0) {
            return;
        }
        requestChange('payable', items[0], () => items.map(
            (item) => revertPcNonPayable(item.row, baselinesRef.current.get(item.key))), items);
    };

    const disallowChecked = () => {
        const items = checkedCandidates;
        if (items.length === 0) {
            return;
        }
        requestChange('disallow', items[0], () => items.map(
            (item) => applyPcNonPayable(item.row, item.nonPayableUnits)), items);
    };

    /**
     * ...and the move back, from a Bill Line Item row: the flag goes true and the row lands on
     * the figures it was found with, `allowed_units_by_proc_construct` included - so the line
     * item returns to the pool offering the construct's own figure rather than what was typed.
     *
     * Three things reach it, not one. The x is the obvious one; the other two are the Payable Qty
     * box settled at the charged units, and the same box emptied back to them. All three say the
     * whole line item is payable, so all three do this: the veto is lifted, the money goes back,
     * and `pcRouteOf` then finds nothing keeping the row in the Bill Line Item table - it drops
     * into the Disallowed pool on the next render with nothing else pressed.
     */
    const makeRowPayable = (item) => {
        requestChange('payable', item,
            () => revertPcNonPayable(item.row, baselinesRef.current.get(item.key)));
    };

    /**
     * The one commit: it sends what the Bill Line Item table holds, and adjudicates.
     *
     * Every row in that table is decided on the way out - priced for the quantity standing in
     * its Payable column, and stamped with the verdict that quantity earns. How far each row
     * goes was settled by `committedNonPayableQuantity` when the table was built, which asks two
     * fields in order:
     *
     *   1. procedure_construct_payable_text still "Payable by Procedure Construct"? Then nobody
     *      has said how far this row goes, and the construct's own ruling is the last word on it.
     *   2. construct_object.allowed false? Then the construct vetoed the line item, and a veto
     *      takes the whole of it: every charged unit is disallowed, and the row leaves as
     *      `procedure_construct_payable: false` with "Non-Payable by Procedure Construct".
     *
     * Anything else keeps the figure the row carries - most of all a quantity settled by hand,
     * which writes its own verdict text as it writes the units and so never reaches step 2.
     *
     * That figure is the complement of the one the Payable column showed, and is what the Saving
     * column totalled,
     * because they were all read off the same call. Two things hang on that:
     *
     *   the verdict  a line item sitting in this table with none of it left allowed leaves as
     *                "Non-Payable by Procedure Construct", never the "Payable by Procedure
     *                Construct" it may still be carrying from the construct that put it here.
     *                One with units left allowed leaves as "Partially Payable by Procedure
     *                Construct", because that is what it is.
     *   the money    a quantity settled in the column writes the units and leaves the money
     *                alone, so `savings` goes stale the moment the box is touched. Pricing it
     *                here is what makes the payload read the way the table does.
     *
     * The Disallowed table below is not touched: a line item left sitting there stays payable
     * and is never sent. Nor is the IRDAI list, which is not this panel's to decide. So this
     * commits the table above them, exactly as that table reads.
     */
    const saveAndAdjudicate = () => {
        if (saveDisabled) {
            return;
        }
        const decided = billLineItems.map((item) => applyPcNonPayable(item.row, item.nonPayableUnits));
        if (decided.length > 0) {
            onCommit?.(decided);
        }
        // The changes have gone out, so the button is no longer armed by them. Cleared here and
        // nowhere else: closing the panel, or reopening it, leaves a staged change staged.
        setHasStagedChange(false);
        setOpen(false);
        setSearch('');
        onAdjudicate?.();
    };

    /**
     * The search box drives all three lists, and one of the three is folded away - so a line
     * item that only turns up in the IRDAI list would be found and never seen. Typing a needle
     * that hits it unfolds it, which is the only thing that opens a list on the reader's behalf.
     *
     * One-way, and no memory of it: it unfolds on a hit and is left alone otherwise, so a reader
     * who folds it back mid-search keeps it folded until the next keystroke that hits it again.
     */
    const runSearch = (value) => {
        setSearch(value);
        const needle = String(value).trim().toLowerCase();
        if (needle.length > 0 && irdaiItems.some((item) => matchesSearch(item, needle))) {
            setIrdaiOpen(true);
        }
    };

    const handleSort = (field) => {
        setSorting((prev) => ({
            field,
            direction: prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc',
        }));
    };

    const attachContainer = React.useCallback((node) => {
        containerRef.current = node;
        if (!node) {
            return;
        }
        // The table only exists while the popup is open, so the position is put back as the
        // view creates it.
        const restoreTo = scrollTopRef.current;
        setTimeout(() => {
            if (containerRef.current) {
                containerRef.current.scrollTop = restoreTo;
            }
        }, 100);
    }, []);

    const handleScroll = (event) => {
        const {scrollTop} = event.currentTarget;
        if (scrollTimerRef.current) {
            clearTimeout(scrollTimerRef.current);
        }
        scrollTimerRef.current = setTimeout(() => {
            scrollTopRef.current = scrollTop;
        }, 150);
    };

    // No tariff to act on - no button. Otherwise it stands on the construct's own findings,
    // and on whatever else is disallowed by hand.
    if (rows.length === 0 && !open) {
        return null;
    }

    return (
        <>
            <Box style={{position: 'relative', display: 'inline-flex'}}>
                <Tooltip
                    describeChild
                    title={(pendingItems.length > 0
                        ? `${formatRupees(disallowedSaving)} still to be saved across`
                        + ` ${pendingItems.length} line item(s)`
                        + (markAllAddedSaving > MONEY_EPSILON
                            ? ` - ${formatRupees(markAllAddedSaving)} still on the table across`
                            + ` ${candidateItems.length} payable line item(s)`
                            : '')
                        : candidateItems.length === 0
                            ? 'Nothing payable is left to disallow - open to review what is already'
                            + ' cut, or to make one payable again'
                            : `Nothing new to disallow on this claim -`
                            + ` ${formatRupees(markAllAddedSaving)} on the table across`
                            + ` ${candidateItems.length} payable line item(s)`)
                        // What the claim already holds, added to what the panel holds now - not
                        // instead of it. Saved once is no longer a reason to stop reading the figures.
                        + (decidedSaving > MONEY_EPSILON
                            ? ` - ${formatRupees(decidedSaving)} already saved across`
                            + ` ${decidedItems.length} line item(s)`
                            : '')}>
                    <Button
                        variant="contained"
                        disableElevation
                        disabled={Boolean(isLoading)}
                        onClick={() => setOpen(true)}
                        sx={attention ? attentionCtaSx : ctaSx}
                    >
                        <Box style={{display: 'flex', alignItems: 'center', gap: '9px'}}>
                            <Box style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                lineHeight: 1.2,
                            }}>
                                <b style={{fontSize: '0.85rem', fontWeight: 700}}>{headline}</b>
                                <em style={{
                                    fontSize: '0.68rem',
                                    fontWeight: 400,
                                    fontStyle: 'normal',
                                    opacity: 0.9,
                                    ...moneyStyle,
                                }}>
                                    {pendingItems.length > 0
                                        ? `${pendingItems.length} line item(s) to disallow`
                                        : decidedItems.length > 0
                                            ? `${decidedItems.length} already saved`
                                            : candidateItems.length > 0
                                                ? `Nothing disallowed — ${candidateItems.length} payable`
                                                : 'Nothing left to disallow'}
                                </em>
                            </Box>
                            <ChevronRightIcon style={{fontSize: '1.1rem', opacity: 0.85}}/>
                        </Box>
                    </Button>
                </Tooltip>

                {attention && attentionMessage && (
                    <Box role="status" sx={attentionCalloutSx}>
                        <Box sx={attentionIconSx}>
                            <LockOutlinedIcon style={{fontSize: '0.95rem'}}/>
                        </Box>
                        <Box style={{display: 'flex', flexDirection: 'column', gap: '2px'}}>
                            <b style={attentionTitleStyle}>
                                {attentionTitle || 'Line item is not payable yet'}
                            </b>
                            <em style={attentionBodyStyle}>{attentionMessage}</em>
                        </Box>
                    </Box>
                )}
            </Box>

            <PcNonPayablesWorkspace
                open={open}
                onClose={() => setOpen(false)}
                claimId={claimId}
                documentFiles={documentFiles}
                title="Procedure Construct Saving"
                subtitle="Disallow what the Procedure Construct should not pay — and pull back what it should"
                figures={figures}
                actions={(
                    <Box style={{display: 'flex', alignItems: 'center', gap: '9px'}}>
                        {dynamicConstruct && (
                            <Tooltip
                                describeChild
                                title={CONSTRUCT_TOOLTIP}>
                                <Button
                                    variant="outlined"
                                    disableElevation
                                    onClick={() => setConstructOpen(true)}
                                    sx={constructButtonSx}
                                >
                                    <Box style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                        <AccountTreeIcon style={{fontSize: '1.05rem'}}/>
                                        <Box style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            lineHeight: 1.2,
                                        }}>
                                            <b style={{fontSize: '0.85rem', fontWeight: 700}}>Medical Construct</b>
                                            <em style={{
                                                fontSize: '0.68rem',
                                                fontWeight: 400,
                                                fontStyle: 'normal',
                                                color: '#7d76c9',
                                            }}>
                                                {constructNote}
                                            </em>
                                        </Box>
                                    </Box>
                                </Button>
                            </Tooltip>
                        )}
                        {/* A disabled MUI button swallows the pointer, so the reason it is
                            disabled has to be hung on a wrapper or nobody ever reads it. */}
                        <Tooltip describeChild title={saveTooltip}>
                            <span style={{display: 'inline-flex'}}>
                                <Button
                                    variant="contained"
                                    disableElevation
                                    disabled={saveDisabled}
                                    onClick={saveAndAdjudicate}
                                    sx={ctaSx}
                                >
                                    <Box style={{display: 'flex', alignItems: 'center', gap: '9px'}}>
                                        <Box style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            lineHeight: 1.2,
                                        }}>
                                            <b style={{fontSize: '0.85rem', fontWeight: 700}}>Save &amp; Adjudicate</b>
                                            <em style={{
                                                fontSize: '0.68rem',
                                                fontWeight: 400,
                                                fontStyle: 'normal',
                                                opacity: 0.9,
                                                ...moneyStyle,
                                            }}>
                                                {saveNote}
                                            </em>
                                        </Box>
                                        <ChevronRightIcon style={{fontSize: '1.1rem', opacity: 0.85}}/>
                                    </Box>
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                )}
            >
                <Paper variant="outlined" style={cardStyle}>
                    <Box style={payableStripStyle}>
                        <Box style={{display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0}}>
                            <ReceiptLongIcon style={{fontSize: '1rem', color: '#1c5fa8', flexShrink: 0}}/>
                            <Typography style={{...stripTitleStyle, color: '#1c5fa8'}}>Non Payable / Partially Payable Bill Line Items</Typography>
                            <Box style={{...countBadgeStyle, background: '#1c5fa8'}}>{listItems.length}</Box>
                            {/* With rows ticked the strip stops describing the table and offers
                                the move instead - that is what the reader is there for by then,
                                and the hint has been read already. */}
                            {checkedBillItems.length > 0 ? (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disableElevation
                                    disabled={disabled}
                                    startIcon={<CloseIcon/>}
                                    onClick={makeCheckedPayable}
                                    sx={bulkPayableButtonSx}
                                >
                                    Make {checkedBillItems.length} payable
                                </Button>
                            ) : (
                                <Typography style={{...hintStyle, color: '#6b8cb4'}}>
                                    {/* Where some of the table is already banked, say so here
                                        rather than leaving the green rows to be worked out. */}
                                    {decidedItems.length > 0
                                        ? `Not payable by Procedure Construct — ${decidedItems.length} already saved`
                                        + `${pendingItems.length > 0 ? `, ${pendingItems.length} to save` : ''}`
                                        : 'Not payable by Procedure Construct — adjust a quantity, or x to make one payable'}
                                </Typography>
                            )}
                        </Box>
                        <TextField
                            size="small"
                            value={search}
                            placeholder="Search line item..."
                            onChange={(e) => runSearch(e.target.value)}
                            sx={searchFieldSx}
                            InputProps={{
                                style: {fontSize: '0.7rem', height: '26px'},
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon style={{fontSize: '0.85rem', color: '#6b8cb4'}}/>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                    <TableContainer
                        ref={attachContainer}
                        onScroll={handleScroll}
                        style={{maxHeight: listHeight}}
                    >
                        <Table stickyHeader size="small" style={{tableLayout: 'fixed'}}>
                            <TableHead>
                                <TableRow style={{height: TABLE_HEADER_HEIGHT}}>
                                    {/* All of what is on screen, or none of it. Indeterminate
                                        while it is some of it, so the header always says what
                                        the list below it holds. */}
                                    <TableCell
                                        padding="checkbox"
                                        style={{...headerCellStyle, width: CHECK_COLUMN_WIDTH}}
                                    >
                                        <Tooltip
                                            describeChild
                                            title={listItems.length === 0
                                                ? ''
                                                : checkedBillItems.length === listItems.length
                                                    ? 'Untick all'
                                                    : `Tick all ${listItems.length} shown`}
                                        >
                                            <Checkbox
                                                size="small"
                                                sx={checkboxSx}
                                                disabled={disabled || listItems.length === 0}
                                                checked={listItems.length > 0
                                                    && checkedBillItems.length === listItems.length}
                                                indeterminate={checkedBillItems.length > 0
                                                    && checkedBillItems.length < listItems.length}
                                                onChange={(event) => toggleAll(
                                                    setCheckedBillKeys, listItems)(event.target.checked)}
                                                inputProps={{'aria-label': 'Tick every line item shown'}}
                                            />
                                        </Tooltip>
                                    </TableCell>
                                    {SORTABLE_COLUMNS.map((column) => (
                                        <TableCell
                                            key={column.key}
                                            align={column.align}
                                            sortDirection={column.sortable !== false && sorting.field === column.key
                                                ? sorting.direction
                                                : false}
                                            style={{
                                                ...headerCellStyle,
                                                width: column.width,
                                                whiteSpace: column.whiteSpace,
                                            }}
                                        >
                                            {column.sortable === false ? column.label : (
                                                <TableSortLabel
                                                    active={sorting.field === column.key}
                                                    direction={sorting.field === column.key
                                                        ? sorting.direction
                                                        : 'desc'}
                                                    onClick={() => handleSort(column.key)}
                                                    IconComponent={sorting.field === column.key
                                                        ? undefined
                                                        : SwapVertIcon}
                                                    hideSortIcon={false}
                                                    sx={sortLabelSx}
                                                >
                                                    {column.label}
                                                </TableSortLabel>
                                            )}
                                        </TableCell>
                                    ))}
                                    <TableCell style={{...headerCellStyle, width: REMOVE_COLUMN_WIDTH}}/>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {listItems.length === 0 && (
                                    <TableRow style={{height: TABLE_ROW_HEIGHT}}>
                                        <TableCell colSpan={SORTABLE_COLUMNS.length + 2}
                                                   style={{...cellStyle, ...emptyNoteStyle, borderBottom: 'none'}}>
                                            {searchNeedle.length > 0
                                                ? 'No disallowed line item matches that search.'
                                                : 'Nothing disallowed on this claim yet.'}
                                        </TableCell>
                                    </TableRow>
                                )}
                                {listItems.map((item) => (
                                    <TableRow
                                        key={item.key}
                                        hover
                                        sx={item.alreadyDecided ? decidedRowSx : rowSx}
                                    >
                                        <TableCell padding="checkbox" style={cellStyle}>
                                            <Checkbox
                                                size="small"
                                                sx={checkboxSx}
                                                disabled={disabled}
                                                checked={checkedBillKeys.has(item.key)}
                                                onChange={() => toggleBillChecked(item.key)}
                                                inputProps={{'aria-label': `Tick ${item.bill_line_item}`}}
                                            />
                                        </TableCell>
                                        <TableCell style={cellStyle}>
                                            <Typography style={{
                                                ...fullNameStyle,
                                                fontSize: '0.79rem',
                                                fontWeight: 600,
                                                color: '#22405f',
                                            }}>
                                                {item.bill_line_item}
                                                {item.alreadyDecided && (
                                                    <Tooltip
                                                        describeChild
                                                        title={'Already non-payable on this claim when this panel'
                                                        + ` was opened — its ${formatRupees(item.saving)} is`
                                                        + ' already saved, so it is not counted in the figure'
                                                        + ' above and Save & Adjudicate will not earn it again.'}
                                                    >
                                                        <Box component="span" style={savedTagStyle}>
                                                            <DoneAllIcon style={{fontSize: '0.66rem'}}/>
                                                            Saved
                                                        </Box>
                                                    </Tooltip>
                                                )}
                                            </Typography>
                                        </TableCell>
                                        <TableCell style={cellStyle}>
                                            <RemarkLine
                                                icon={<AccountTreeIcon style={{fontSize: '0.78rem'}}/>}
                                                title="Construct Remarks"
                                                text={item.constructRemarks}
                                                colour="#8195ad"
                                            />
                                            <RemarkLine
                                                icon={<EditNoteIcon style={{fontSize: '0.85rem'}}/>}
                                                label="ABHI Remarks"
                                                text={item.remarks}
                                                colour="#41597a"
                                                onEdit={disabled ? undefined : () => editRemarks(item)}
                                            />
                                        </TableCell>
                                        <TableCell align="right"
                                                   style={{...cellStyle, paddingRight: '6px', whiteSpace: 'nowrap'}}>
                                            <Tooltip title={describeApplied(item)}>
                                                <Box style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'flex-end',
                                                    gap: '6px',
                                                }}>
                                                    <span style={{
                                                        fontSize: '0.78rem',
                                                        color: '#8195ad',
                                                        ...moneyStyle,
                                                    }}>
                                                        {item.charged} /
                                                    </span>
                                                    <PayableQuantityInput
                                                        charged={item.charged}
                                                        quantity={item.payableUnits}
                                                        revision={changeRevision}
                                                        isCut={item.isCut}
                                                        disabled={disabled}
                                                        onSettle={(quantity) => settleQuantity(item, quantity)}
                                                        onClear={() => clearQuantity(item)}
                                                    />
                                                </Box>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="right" style={{
                                            ...cellStyle,
                                            ...moneyStyle,
                                            paddingLeft: '4px',
                                            paddingRight: '6px',
                                            fontWeight: 700,
                                            // Rose for money this press will take off the claim,
                                            // green for money already off it. The column totals
                                            // to more than the card above, and this is the whole
                                            // of the difference: the green rows are not in it.
                                            color: item.alreadyDecided ? '#1b7f4b' : '#a53535',
                                            whiteSpace: 'nowrap',
                                        }}>
                                            <Tooltip title={describeApplied(item)}>
                                                <span>{formatRupees(item.saving)}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="center" style={{...cellStyle, padding: '4px 4px'}}>
                                            <IconButton
                                                size="small"
                                                aria-label="Make this line item payable again"
                                                disabled={disabled}
                                                onClick={() => makeRowPayable(item)}
                                                sx={removeButtonSx}
                                            >
                                                <CloseIcon style={{fontSize: '0.8rem'}}/>
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <Paper variant="outlined" style={{...cardStyle, marginTop: '12px'}}>
                    <Box style={disallowedStripStyle}>
                        <Box style={{display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0}}>
                            {/* This list has no header row of its own, so its select-all lives on
                                the strip - in the same place, at the same width, as the one at the
                                head of the table above. */}
                            <Box style={{width: CHECK_COLUMN_WIDTH, flexShrink: 0}}>
                                <Tooltip
                                    describeChild
                                    title={searchedCandidates.length === 0
                                        ? ''
                                        : checkedCandidates.length === searchedCandidates.length
                                            ? 'Untick all'
                                            : `Tick all ${searchedCandidates.length} shown`}
                                >
                                    <Checkbox
                                        size="small"
                                        sx={checkboxSx}
                                        disabled={disabled || searchedCandidates.length === 0}
                                        checked={searchedCandidates.length > 0
                                            && checkedCandidates.length === searchedCandidates.length}
                                        indeterminate={checkedCandidates.length > 0
                                            && checkedCandidates.length < searchedCandidates.length}
                                        onChange={(event) => toggleAll(
                                            setCheckedCandidateKeys, searchedCandidates)(event.target.checked)}
                                        inputProps={{'aria-label': 'Tick every payable line item shown'}}
                                    />
                                </Tooltip>
                            </Box>
                            <Typography style={{...stripTitleStyle, color: '#a53535'}}>Payable Bill Line Items</Typography>
                            <Box style={{...countBadgeStyle, background: '#c25151'}}>{searchedCandidates.length}</Box>
                            {checkedCandidates.length > 0 ? (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disableElevation
                                    disabled={disabled}
                                    startIcon={<AddIcon/>}
                                    onClick={disallowChecked}
                                    sx={bulkDisallowButtonSx}
                                >
                                    Disallow {checkedCandidates.length}
                                </Button>
                            ) : (
                                <Typography style={{...hintStyle, color: '#b97070'}}>
                                    Still payable — add one to disallow it and move it up
                                </Typography>
                            )}
                        </Box>
                        <Typography style={{
                            ...moneyStyle,
                            flexShrink: 0,
                            fontSize: '0.78rem',
                            fontWeight: 700,
                            color: '#a53535',
                        }}>
                            {markAllAddedSaving > MONEY_EPSILON ? `+ ${formatRupees(markAllAddedSaving)}` : '—'}
                        </Typography>
                    </Box>
                    <Box style={{
                        maxHeight: listHeight,
                        overflowY: 'auto',
                    }}>
                        {searchedCandidates.length === 0 && (
                            <Typography style={emptyNoteStyle}>
                                {searchNeedle.length > 0
                                    ? 'No payable line item matches that search.'
                                    : 'Nothing left payable to disallow.'}
                            </Typography>
                        )}
                        {searchedCandidates.map((item) => (
                            <Box key={item.key} sx={disallowedRowSx}>
                                <Box style={{width: CHECK_COLUMN_WIDTH, flexShrink: 0}}>
                                    <Checkbox
                                        size="small"
                                        sx={checkboxSx}
                                        disabled={disabled}
                                        checked={checkedCandidateKeys.has(item.key)}
                                        onChange={() => toggleCandidateChecked(item.key)}
                                        inputProps={{'aria-label': `Tick ${item.bill_line_item}`}}
                                    />
                                </Box>
                                <Typography style={{
                                    ...fullNameStyle,
                                    width: '17rem',
                                    flexShrink: 0,
                                    fontSize: '0.78rem',
                                    fontWeight: 600,
                                    color: '#2f4560',
                                }}>
                                    {item.bill_line_item}
                                </Typography>
                                {/* Both remarks, stacked and read only: the construct's own
                                    words above whatever was written on the row. Both go
                                    unlabelled - the sentence alone, with the icon and the
                                    tooltip to say whose words they are - because this row is
                                    read at a glance on the way to Add, and a label costs more
                                    width here than it earns. Neither is editable: the line
                                    item is still payable, so there is no change on it for a
                                    remark to be the reason for, and the note it carries is
                                    only here to be read before Add is pressed. A row carrying
                                    neither still prints the dash, so the column is never an
                                    unexplained gap. */}
                                <Box style={{flexGrow: 1, minWidth: 0}}>
                                    <RemarkLine
                                        icon={<AccountTreeIcon style={{fontSize: '0.78rem'}}/>}
                                        title="Construct Remarks"
                                        text={item.constructRemarks}
                                        colour="#8d9aa7"
                                    />
                                    <RemarkLine
                                        icon={<EditNoteIcon style={{fontSize: '0.85rem'}}/>}
                                        title="ABHI Remarks"
                                        text={item.remarks}
                                        colour="#6b7f99"
                                    />
                                    {hasNoRemark(item) && (
                                        <Typography style={{
                                            ...oneLineStyle,
                                            fontSize: '0.72rem',
                                            color: '#8d9aa7',
                                        }}>
                                            —
                                        </Typography>
                                    )}
                                </Box>
                                <Tooltip title={describeSplit(item)}>
                                    <Typography style={{
                                        ...moneyStyle,
                                        width: '5rem',
                                        flexShrink: 0,
                                        textAlign: 'right',
                                        fontSize: '0.75rem',
                                        color: '#6b7f99',
                                        whiteSpace: 'nowrap',
                                    }}>
                                        {item.charged} / {item.payableUnits}
                                    </Typography>
                                </Tooltip>
                                <Tooltip title={describeSplit(item)}>
                                    <Typography style={{
                                        ...moneyStyle,
                                        width: '6.5rem',
                                        flexShrink: 0,
                                        textAlign: 'right',
                                        fontSize: '0.78rem',
                                        fontWeight: 700,
                                        color: item.gain > MONEY_EPSILON ? '#1b7f4b' : '#8d9aa7',
                                    }}>
                                        {gainLabel(item.gain)}
                                    </Typography>
                                </Tooltip>
                                <Box style={{width: ADD_COLUMN_WIDTH, flexShrink: 0, textAlign: 'right'}}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        aria-label="Disallow this line item"
                                        disabled={disabled}
                                        startIcon={<AddIcon/>}
                                        onClick={() => disallowRow(item)}
                                        sx={addButtonSx}
                                    >
                                        Add
                                    </Button>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Paper>

                {/* The third list, and the one nothing can be done to: what IRDAI does not pay.
                    It is last because it is read last - the two lists above are the decision,
                    this one is the rest of the claim's non-payables, here so they can be
                    reviewed in one place instead of one screen away in the tariff sheet.

                    Every control the lists above carry is absent, not disabled: there is no
                    decision to grey out. Its amount is what the claim is already not paying,
                    which is why it is printed in the amber rather than in the green the rows
                    above use for money still on the table. */}
                {irdaiItems.length > 0 && (
                    <Paper variant="outlined" style={{...cardStyle, marginTop: '12px'}}>
                        {/* Folded, the strip is the whole card - so it has to carry the figure
                            and the count, not just the title. What the fold hides is which line
                            items they are, never how much or how many: a reader who never opens
                            it still leaves knowing what IRDAI took off this claim. */}
                        <Tooltip
                            describeChild
                            title={irdaiOpen
                                ? 'Hide the line items IRDAI does not pay'
                                : `Show the ${irdaiItems.length} line item(s) IRDAI does not pay`
                                + ` — ${formatRupees(irdaiNonPayableTotal)} already off this claim`}
                        >
                            <Box
                                role="button"
                                tabIndex={0}
                                aria-expanded={irdaiOpen}
                                onClick={() => setIrdaiOpen((shown) => !shown)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        setIrdaiOpen((shown) => !shown);
                                    }
                                }}
                                sx={{
                                    ...irdaiHeaderSx,
                                    // Folded, the strip is the last thing in the card, and its
                                    // own line under the card's border read as two.
                                    borderBottom: irdaiOpen ? irdaiHeaderSx.borderBottom : 'none',
                                }}
                            >
                                <Box style={{display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0}}>
                                    {/* The chevron points down when there is something below to
                                        come, and turns to point at the list once it is open. */}
                                    <ExpandMoreIcon style={{
                                        fontSize: '1rem',
                                        color: '#8a6d1f',
                                        flexShrink: 0,
                                        transform: irdaiOpen ? 'rotate(180deg)' : 'none',
                                        transition: 'transform 180ms ease',
                                    }}/>
                                    <GavelIcon style={{fontSize: '0.95rem', color: '#8a6d1f', flexShrink: 0}}/>
                                    <Typography style={{...stripTitleStyle, color: '#8a6d1f'}}>IRDAI Non-Payable Bill Line Items</Typography>
                                    <Box style={{...countBadgeStyle, background: '#b08829'}}>{searchedIrdaiItems.length}</Box>
                                    <Typography style={{...hintStyle, color: '#a08a4f'}}>
                                        {irdaiOpen
                                            ? 'Not payable under IRDAI — already off the claim, and nothing here to decide'
                                            : 'Not payable under IRDAI — already off the claim. Click to review'}
                                    </Typography>
                                </Box>
                                <Typography style={{
                                    ...moneyStyle,
                                    flexShrink: 0,
                                    fontSize: '0.78rem',
                                    fontWeight: 700,
                                    color: '#8a6d1f',
                                }}>
                                    {formatRupees(irdaiNonPayableTotal)}
                                </Typography>
                            </Box>
                        </Tooltip>
                        {/* unmountOnExit: folded away it is not there at all, so a claim with a
                            long amber list costs nothing to scroll past. */}
                        <Collapse in={irdaiOpen} unmountOnExit>
                            <Box style={{
                                maxHeight: listHeight,
                                overflowY: 'auto',
                            }}>
                                {searchedIrdaiItems.length === 0 && (
                                    <Typography style={emptyNoteStyle}>
                                        No IRDAI non-payable line item matches that search.
                                    </Typography>
                                )}
                                {searchedIrdaiItems.map((item) => (
                                    <Box key={item.key} sx={irdaiRowSx}>
                                        {/* The tick column, held empty: nothing in this list can
                                            be moved, so there is nothing to tick - but the three
                                            lists still line up down their left edge. */}
                                        <Box style={{width: CHECK_COLUMN_WIDTH, flexShrink: 0}}/>
                                        <Typography style={{
                                            ...fullNameStyle,
                                            width: '17rem',
                                            flexShrink: 0,
                                            fontSize: '0.78rem',
                                            fontWeight: 600,
                                            color: '#2f4560',
                                        }}>
                                            {item.bill_line_item}
                                        </Typography>
                                        {/* Both remarks again, read the same way as the list above
                                            reads them, so a note written on a line item is on the
                                            screen wherever that line item ends up. */}
                                        <Box style={{flexGrow: 1, minWidth: 0}}>
                                            <RemarkLine
                                                icon={<AccountTreeIcon style={{fontSize: '0.78rem'}}/>}
                                                title="Construct Remarks"
                                                text={item.constructRemarks}
                                                colour="#a08a4f"
                                            />
                                            <RemarkLine
                                                icon={<EditNoteIcon style={{fontSize: '0.85rem'}}/>}
                                                title="ABHI Remarks"
                                                text={item.remarks}
                                                colour="#8a7333"
                                            />
                                            {hasNoRemark(item) && (
                                                <Typography style={{
                                                    ...oneLineStyle,
                                                    fontSize: '0.72rem',
                                                    color: '#a08a4f',
                                                }}>
                                                    —
                                                </Typography>
                                            )}
                                        </Box>
                                        <Tooltip title={describeIrdaiRow(item)}>
                                            <Typography style={{
                                                ...moneyStyle,
                                                width: '5rem',
                                                flexShrink: 0,
                                                textAlign: 'right',
                                                fontSize: '0.75rem',
                                                color: '#a08a4f',
                                                whiteSpace: 'nowrap',
                                            }}>
                                                {item.charged} unit(s)
                                            </Typography>
                                        </Tooltip>
                                        <Tooltip title={describeIrdaiRow(item)}>
                                            <Typography style={{
                                                ...moneyStyle,
                                                width: '6.5rem',
                                                flexShrink: 0,
                                                textAlign: 'right',
                                                fontSize: '0.78rem',
                                                fontWeight: 700,
                                                color: '#8a6d1f',
                                            }}>
                                                {formatRupees(item.amount)}
                                            </Typography>
                                        </Tooltip>
                                        {/* The width the Add button takes on the row above, held
                                            empty, so the money columns of the two lists line up
                                            instead of sitting a button's width apart. */}
                                        <Box style={{width: ADD_COLUMN_WIDTH, flexShrink: 0}}/>
                                    </Box>
                                ))}
                            </Box>
                        </Collapse>
                    </Paper>
                )}


                {Math.abs(sessionAddedSaving) > MONEY_EPSILON && (
                    <Typography style={{
                        ...moneyStyle,
                        fontSize: '0.7rem',
                        color: sessionAddedSaving > 0 ? '#1b7f4b' : '#a53535',
                        padding: '8px 2px 0',
                    }}>
                        {sessionAddedSaving > 0 ? '+' : '−'} {formatRupees(Math.abs(sessionAddedSaving))} against
                        {' '}{formatRupees(existingSaving || 0)} when this panel was opened — save the tariff sheet
                        {' '}to keep it.
                    </Typography>
                )}
            </PcNonPayablesWorkspace>

            <ConstructDialog
                open={constructOpen}
                onClose={() => setConstructOpen(false)}
                construct={dynamicConstruct}
                deduction={constructDeduction}
            />

            <ChangeRemarkDialog
                pending={pendingChange}
                onCancel={cancelChange}
                onConfirm={confirmChange}
            />
        </>
    );
}

export default PcNonPayables;
